import { axRunChatSession } from './chatSession.js';
import { processSessionPartialResult } from './response/streaming.js';
import { createStructuredDelta } from './response/structuredDelta.js';
import { axValidateToolArguments } from './toolArguments.js';
import type { DeltaOut } from './types.js';
import { outputValueDescriptions } from './valueDescriptions.js';
// ReadableStream is available globally in modern browsers and Node.js 16+

import {
  type Context,
  context,
  type Meter,
  type Span,
  SpanKind,
  SpanStatusCode,
  trace,
} from '@opentelemetry/api';

import { logResultPickerUsed } from '../ai/debug.js';
import {
  type AxPromptMetrics,
  countChatPromptContentChars,
} from '../ai/promptMetrics.js';
import type {
  AxAIService,
  AxAIServiceOptions,
  AxChatRequest,
  AxChatResponse,
  AxChatResponseResult,
  AxFunction,
  AxStructuredOutputMode,
  AxStructuredOutputRung,
} from '../ai/types.js';
import { axMergeUsageContexts } from '../ai/usage.js';
import { axResolveMCPExecutionContext } from '../mcp/execution.js';
import { AxMemory } from '../mem/memory.js';
import type { AxAIMemory } from '../mem/types.js';
import { mergeAbortSignals } from '../util/abort.js';
import {
  AxAIRefusalError,
  AxAIServiceAbortedError,
  AxAIServiceNetworkError,
  AxAIServiceStatusError,
  AxAIServiceStreamTerminatedError,
  AxAIServiceTimeoutError,
} from '../util/apicall.js';
import { createHash } from '../util/crypto.js';
import {
  type AxRuntimeHookFramedOptions,
  axGetRuntimeHookFrame,
  axRuntimeHookFrame,
  axStartSpanFailOpen,
} from '../util/telemetry.js';
import {
  type AxAssertion,
  AxAssertionError,
  type AxStreamingAssertion,
  AxStreamingAssertionError,
  assertAssertions,
} from './asserts.js';
import { renderAudioOutputArtifacts } from './audioArtifacts.js';
import {
  type HandleErrorForGenerateArgs,
  handleRefusalErrorForGenerate,
  handleValidationErrorForGenerate,
  ValidationError,
} from './errors.js';
import { validateStructuredOutputValues } from './extract.js';
import {
  type AxFieldProcessor,
  processFieldProcessors,
} from './fieldProcessor.js';
import {
  type AxChatResponseFunctionCall,
  AxStopFunctionCallException,
  createFunctionConfig,
  parseFunctions,
  processFunctions,
} from './functions.js';
import { axGlobals } from './globals.js';
import { toJsonSchema } from './jsonSchema.js';
import {
  type AxGenMetricsInstruments,
  getOrCreateGenMetricsInstruments,
  mergeCustomLabels,
  recordErrorCorrectionMetric,
  recordFieldProcessingMetric,
  recordFunctionCallingMetric,
  recordGenerationMetric,
  recordMultiStepMetric,
  recordPerformanceMetric,
  recordSamplesMetric,
  recordSignatureComplexityMetrics,
  recordStreamingMetric,
} from './metrics.js';
import {
  type AxOptimizableComponent,
  axOptimizableValidators,
} from './optimizable.js';
import {
  processResponse,
  processStreamingResponse,
  shouldContinueSteps,
} from './processResponse.js';
import { AxProgram } from './program.js';
import { AxPromptTemplate, type AxRenderedPrompt } from './prompt.js';
import { areValuesEqual } from './response/structuredDelta.js';
import type { InternalAxGenState } from './response/types.js';
import { selectFromSamples, selectFromSamplesInMemory } from './samples.js';
import { createSelfTuningFunction } from './selfTuning.js';
import { type AxIField, AxSignature, type AxSignatureConfig } from './sig.js';
import { SignatureToolCallingManager } from './signatureToolCalling.js';
import { AxStepContextImpl } from './stepContext.js';
import type {
  AsyncGenDeltaOut,
  AxChatLogEntry,
  AxChatLogMessage,
  AxGenDeltaOut,
  AxGenOut,
  AxGenStreamingOut,
  AxProgramExamples,
  AxProgramForwardOptions,
  AxProgramForwardOptionsWithModels,
  AxProgrammable,
  AxProgramStreamingForwardOptionsWithModels,
  AxResultPickerFunction,
  AxSelfTuningConfig,
  AxSetExamplesOptions,
} from './types.js';
import { mergeDeltas } from './util.js';
import {
  validateNumberConstraints,
  validateStringConstraints,
  validateURL,
} from './validators.js';

const axSessionOutputVersion = Symbol('ax.sessionOutputVersion');

// Forward options the AxGen constructor sets as defaults for every call: a
// value the call gives wins. modelConfig and customLabels merge key by key.
const constructorDefaultOptionKeys = [
  'model',
  'sampleCount',
  'showThoughts',
  'thinkingTokenBudget',
  'stepHooks',
  'onFunctionCall',
  'disableMemoryCleanup',
  'selfTuning',
  'asyncMode',
  'resultPicker',
  'control',
  'stream',
  'sessionId',
  'abortSignal',
  'timeout',
  'fetch',
  'webSocket',
  'traceContext',
  'executionPath',
  'eventContext',
  'speech',
  'excludeContentFromTrace',
  'serviceTier',
  'verbose',
  'beta',
  'corsProxy',
  'includeRequestBodyInErrors',
  'promptCacheRetention',
] as const;

// Forward options that belong to one run. A program that builds an AxGen to
// run inside it passes them to each forward, so they must not sit in the
// AxGen's constructor, where they would now be defaults for every call.
const runScopedOptionKeys = [
  'control',
  'abortSignal',
  'sessionId',
  'stream',
  'eventContext',
  'traceContext',
  'executionPath',
] as const;

/** Constructor options without the run-scoped forward options. */
export function withoutRunScopedOptions<O extends object | undefined>(
  options: O
): O {
  if (!options) return options;
  const rest: Record<string, unknown> = { ...options };
  for (const key of runScopedOptionKeys) delete rest[key];
  return rest as O;
}
const STRUCTURED_OUTPUT_FUNCTION_NAME = '__axOutput';
const LEGACY_STRUCTURED_OUTPUT_FUNCTION_NAME = '__finalResult';

const isReservedStructuredOutputFunctionName = (name: string): boolean =>
  name === STRUCTURED_OUTPUT_FUNCTION_NAME ||
  name === LEGACY_STRUCTURED_OUTPUT_FUNCTION_NAME;

const assertNoReservedStructuredOutputFunctions = (
  functions: readonly AxFunction[]
): void => {
  const collision = functions.find((fn) =>
    isReservedStructuredOutputFunctionName(fn.name)
  );
  if (collision) {
    throw new Error(
      `Function name '${collision.name}' is reserved for Ax structured-output handling.`
    );
  }
};

const selectStructuredOutputRung = (
  signature: Readonly<AxSignature>,
  features:
    | Readonly<{
        structuredOutputs?: boolean;
        structuredOutputModes?: readonly AxStructuredOutputRung[];
        requiresStructuredOutput?: boolean;
        functions?: boolean;
      }>
    | undefined,
  mode: AxStructuredOutputMode,
  providerLabel: string
): AxStructuredOutputRung | undefined => {
  if (!signature.hasComplexFields() && !features?.requiresStructuredOutput)
    return undefined;

  // Missing capability flags belong to custom/unknown clients. Preserve the
  // historical compatibility assumption that those clients support native
  // structured outputs and functions unless they explicitly say otherwise.
  const advertisedModes = features?.structuredOutputModes;
  const hasAdvertisedModes = advertisedModes !== undefined;
  const supportsNative = hasAdvertisedModes
    ? advertisedModes.includes('native')
    : features?.structuredOutputs !== false;
  const supportsFunctions = hasAdvertisedModes
    ? advertisedModes.includes('function')
    : features?.functions !== false;
  const supportsJSONObject = hasAdvertisedModes
    ? advertisedModes.includes('json_object')
    : true;

  if (mode === 'native') {
    if (!supportsNative) {
      throw new Error(
        `Structured output mode 'native' requires native JSON Schema support, but ${providerLabel} advertises structuredOutputs=false.`
      );
    }
    return 'native';
  }

  if (mode === 'function') {
    if (!supportsFunctions) {
      throw new Error(
        `Structured output mode 'function' requires function calling support, but ${providerLabel} advertises functions=false.`
      );
    }
    return 'function';
  }

  if (mode === 'json_object') {
    if (!supportsJSONObject) {
      throw new Error(
        `Structured output mode 'json_object' requires JSON object response-format support, but ${providerLabel} does not advertise it.`
      );
    }
    return 'json_object';
  }

  if (hasAdvertisedModes && advertisedModes.length === 0) {
    throw new Error(
      `Structured output is not verified for ${providerLabel}; add an exact modelInfo override to opt in.`
    );
  }

  if (!hasAdvertisedModes && supportsNative) return 'native';

  const providerVisibleFields = signature
    .getOutputFields()
    .filter((field) => !field.isInternal);
  const onlyField = providerVisibleFields[0];
  const isRequiredSingletonString =
    providerVisibleFields.length === 1 &&
    onlyField !== undefined &&
    !onlyField.isOptional &&
    !onlyField.type?.isArray &&
    (onlyField.type?.name === undefined ||
      onlyField.type.name === 'string' ||
      onlyField.type.name === 'code');

  if (isRequiredSingletonString && !supportsNative && supportsJSONObject) {
    return 'json_object';
  }
  if (hasAdvertisedModes) return advertisedModes[0];
  if (supportsFunctions) return 'function';
  return 'json_object';
};

export type {
  AxResponseHandlerArgs,
  InternalAxGenState,
} from './response/types.js';

export type AxGenerateResult<OUT> = OUT & {
  thought?: string;
};

export interface AxStreamingEvent<T> {
  event: 'delta' | 'done' | 'error';
  data: {
    contentDelta?: string;
    partialValues?: Partial<T>;
    error?: string;
    functions?: AxChatResponseFunctionCall[];
  };
}

type AxChatResponseLogMetadata = Pick<
  AxChatResponse,
  | 'sessionId'
  | 'remoteId'
  | 'remoteRequestId'
  | 'remoteSessionId'
  | 'providerMetadata'
>;

// Merges a delta into values as a consumer does: text and arrays append, any
// other value replaces.
function mergeDeltaValues(
  values: Record<string, unknown>,
  delta: Readonly<Record<string, unknown>>
): void {
  for (const [key, value] of Object.entries(delta)) {
    const previous = values[key];
    values[key] =
      typeof value === 'string'
        ? String(previous ?? '') + value
        : Array.isArray(value)
          ? [...(Array.isArray(previous) ? previous : []), ...value]
          : value;
  }
}

export class AxGen<IN = any, OUT extends AxGenOut = any>
  extends AxProgram<IN, OUT>
  implements AxProgrammable<IN, OUT>
{
  public clone = (): AxGen<IN, OUT> => {
    const cloned = new AxGen(
      this.signature as AxSignature<IN & Record<string, any>, OUT>,
      this.options
    );
    cloned.asserts = [...this.asserts];
    cloned.streamingAsserts = [...this.streamingAsserts];
    cloned.fieldProcessors = [...this.fieldProcessors];
    cloned.streamingFieldProcessors = [...this.streamingFieldProcessors];
    return cloned;
  };
  private promptTemplate: AxPromptTemplate;
  private asserts: AxAssertion<OUT>[];
  private streamingAsserts: AxStreamingAssertion[];
  private options?: Omit<AxProgramForwardOptions<any>, 'functions'>;
  private functions: AxFunction[];
  private functionComponentIds = new WeakMap<AxFunction, string>();
  private fieldProcessors: AxFieldProcessor[] = [];
  private streamingFieldProcessors: AxFieldProcessor[] = [];
  private excludeContentFromTrace = false;
  private thoughtFieldName: string;
  private signatureToolCallingManager?: SignatureToolCallingManager;
  private structuredOutputFunctionFallback = false;
  private structuredOutputRung?: AxStructuredOutputRung;
  private activeAbortControllers = new Set<AbortController>();
  private _stopRequested = false;
  private chatLog: AxChatLogEntry[] = [];

  constructor(
    signature:
      | string
      | Readonly<AxSignatureConfig>
      | Readonly<AxSignature<IN & Record<string, any>, OUT>>,
    options?: Readonly<AxProgramForwardOptions<any>>
  ) {
    super(signature, {
      description: options?.description,
      traceLabel: options?.traceLabel,
    });

    this.options = options;
    this.thoughtFieldName = options?.thoughtFieldName ?? 'thought';
    const promptTemplateOptions = {
      functions: options?.functions,
      thoughtFieldName: this.thoughtFieldName,
      customTemplate: options?.customTemplate,
      includeOptionalInputFieldsInSystemPrompt:
        options?.includeOptionalInputFieldsInSystemPrompt,
    };
    this.promptTemplate = new (options?.promptTemplate ?? AxPromptTemplate)(
      this.signature,
      promptTemplateOptions
    );
    this.asserts = [...(options?.asserts ?? [])] as AxAssertion<OUT>[];
    this.streamingAsserts = [...(options?.streamingAsserts ?? [])];
    this.excludeContentFromTrace = options?.excludeContentFromTrace ?? false;
    this.functions = options?.functions
      ? parseFunctions(options.functions)
      : [];
    this.ensureFunctionComponentIds();
    this.usage = [];
  }

  /**
   * Stops an in-flight generation. Causes `forward()` / `streamingForward()`
   * to throw `AxAIServiceAbortedError`.
   */
  public stop(): void {
    this._stopRequested = true;
    // Abort all in-flight executions started by this AxGen instance.
    for (const controller of this.activeAbortControllers) {
      controller.abort('Stopped by user');
    }
  }

  public setInstruction(instruction: string): void {
    this.promptTemplate.setInstruction(instruction);
  }

  public getInstruction(): string | undefined {
    return this.promptTemplate.getInstruction();
  }

  public clearInstruction(): void {
    this.promptTemplate.clearInstruction();
  }

  private static stableFunctionComponentBase(name: string): string {
    const normalized = name
      .trim()
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase();
    return normalized || 'tool';
  }

  private ensureFunctionComponentIds(): void {
    const used = new Set<string>();
    for (const fn of this.functions) {
      const existing = this.functionComponentIds.get(fn);
      if (existing) {
        fn.componentId = existing;
        used.add(existing);
        continue;
      }

      const base = AxGen.stableFunctionComponentBase(fn.name);
      let id = base;
      let suffix = 2;
      while (used.has(id)) {
        id = `${base}_${suffix++}`;
      }
      used.add(id);
      this.functionComponentIds.set(fn, id);
      fn.componentId = id;
    }
  }

  private validateFunctionNameCandidate(
    fn: AxFunction,
    value: string
  ): true | string {
    const shape = axOptimizableValidators.snakeCaseIdentifier(32)(value);
    if (shape !== true) return shape;
    const trimmed = value.trim();
    const collides = this.functions.some(
      (other) => other !== fn && other.name === trimmed
    );
    return collides ? 'identifier must be distinct from sibling tools' : true;
  }

  protected override localOptimizableComponents(): readonly AxOptimizableComponent[] {
    const out: AxOptimizableComponent[] = [
      ...super.localOptimizableComponents(),
    ];
    const id = this.getId();
    this.ensureFunctionComponentIds();
    for (const fn of this.functions) {
      const functionId = this.functionComponentIds.get(fn)!;
      out.push({
        key: `${id}::fn:${functionId}:desc`,
        kind: 'fn-desc',
        current: fn.description ?? '',
        traceId: functionId,
        description: `Tool description shown to caller LLM to decide WHEN to invoke \`${fn.name}\`.`,
        constraints:
          'Concise; describe the tool’s purpose and inputs in one or two sentences.',
        preserve: [],
        maxLength: 320,
        validate: axOptimizableValidators.nonEmpty(),
      });
      out.push({
        key: `${id}::fn:${functionId}:name`,
        kind: 'fn-name',
        current: fn.name,
        traceId: functionId,
        description: `Identifier the LLM uses to invoke this tool. Renaming changes how the model addresses it.`,
        constraints:
          'snake_case identifier, ≤32 chars, distinct from siblings.',
        maxLength: 32,
        format: 'snake_case',
        validate: (value) => this.validateFunctionNameCandidate(fn, value),
      });
    }
    return out;
  }

  protected override applyLocalOptimizedComponents(
    updates: Readonly<Record<string, string>>
  ): void {
    super.applyLocalOptimizedComponents(updates);

    const id = this.getId();
    this.ensureFunctionComponentIds();
    const renames: Array<{ from: string; to: string }> = [];

    for (const fn of this.functions) {
      const functionId = this.functionComponentIds.get(fn)!;
      const descKey = `${id}::fn:${functionId}:desc`;
      if (typeof updates[descKey] === 'string') {
        fn.description = updates[descKey]!;
      }
    }

    const proposedNames = new Map<AxFunction, string>();
    for (const fn of this.functions) {
      const functionId = this.functionComponentIds.get(fn)!;
      const nameKey = `${id}::fn:${functionId}:name`;
      const proposed = updates[nameKey];
      if (typeof proposed !== 'string' || proposed === fn.name) continue;

      const trimmed = proposed.trim();
      const valid = axOptimizableValidators.snakeCaseIdentifier(32)(trimmed);
      if (valid !== true) continue;
      proposedNames.set(fn, trimmed);
    }

    if (proposedNames.size > 0) {
      const finalNames = this.functions.map(
        (fn) => proposedNames.get(fn) ?? fn.name
      );
      if (new Set(finalNames).size === finalNames.length) {
        for (const [fn, proposed] of proposedNames.entries()) {
          renames.push({ from: fn.name, to: proposed });
          fn.name = proposed;
        }
      }
    }

    if (renames.length > 0) {
      // Strip stale demo entries — function-call traces under the old name
      // would mislead the LLM if the underlying tool now has a different
      // identifier. Cheaper to drop than to rewrite ambiguously.
      this.demos = [];
    }
  }

  private getEffectiveContextCache(
    ai: Readonly<AxAIService>,
    options?: Readonly<Partial<AxProgramForwardOptions<any>>>
  ): AxProgramForwardOptions<any>['contextCache'] | undefined {
    return (
      options?.contextCache ??
      this.options?.contextCache ??
      ai.getOptions().contextCache
    );
  }

  private async renderPromptWithMetricsForInternalUse(
    ai: Readonly<AxAIService>,
    values: IN,
    callOptions?: Readonly<
      Partial<Omit<AxProgramForwardOptions<any>, 'functions'>>
    >,
    functionsOverride?: AxFunction[]
  ): Promise<{
    prompt: AxChatRequest['chatPrompt'];
    promptMetrics?: AxPromptMetrics;
  }> {
    const options = this.withConstructorDefaults(callOptions);
    const promptTemplateClass =
      options?.promptTemplate ??
      this.options?.promptTemplate ??
      AxPromptTemplate;
    const sourceFunctions = functionsOverride ?? this.functions;
    if (functionsOverride === undefined) {
      assertNoReservedStructuredOutputFunctions(sourceFunctions);
    }
    const mutableFunctions = sourceFunctions.filter(
      (fn) => !isReservedStructuredOutputFunctionName(fn.name)
    );
    const functionCallMode =
      options?.functionCallMode ?? this.options?.functionCallMode ?? 'auto';
    const hasFunctions = mutableFunctions.length > 0;
    let signatureToolCallingManager: SignatureToolCallingManager | undefined;
    if (
      hasFunctions &&
      ai.getFeatures(options?.model).functionEmulation === false &&
      !ai.getFeatures(options?.model).functions
    ) {
      throw new Error(
        `${ai.getName()} does not support tools or prompt tool emulation`
      );
    }

    if (hasFunctions && functionCallMode === 'prompt') {
      signatureToolCallingManager = new SignatureToolCallingManager(
        mutableFunctions
      );
    }

    if (
      hasFunctions &&
      functionCallMode === 'auto' &&
      !ai.getFeatures(options?.model).functions
    ) {
      signatureToolCallingManager = new SignatureToolCallingManager(
        mutableFunctions
      );
    }

    let signature = AxSignature.from(this.signature);
    if (signatureToolCallingManager) {
      signature = signatureToolCallingManager.processSignature(signature);
    }

    const features = ai.getFeatures?.(options?.model);
    const structuredOutputMode =
      options?.structuredOutputMode ??
      this.options?.structuredOutputMode ??
      'auto';
    const structuredOutputRung = selectStructuredOutputRung(
      signature,
      features,
      structuredOutputMode,
      `${ai.getName()} (${String(options?.model ?? 'default model')})`
    );
    const structuredOutputFunctionFallback =
      structuredOutputRung === 'function';
    if (structuredOutputFunctionFallback) {
      const syntheticFunction: AxFunction = {
        name: STRUCTURED_OUTPUT_FUNCTION_NAME,
        description:
          'Emit the complete structured program output using the declared argument shape.',
        parameters: toJsonSchema(signature.getOutputFields()),
        func: async () => 'done',
      };
      mutableFunctions.push(syntheticFunction);
    }
    const providerIgnoreBreakpoints =
      ai.getFeatures?.(options?.model)?.caching?.cacheBreakpoints === false;
    const contextCache = this.getEffectiveContextCache(ai, options);
    const promptTemplate = new promptTemplateClass(signature, {
      structuredOutput: structuredOutputRung !== undefined,
      functions: signatureToolCallingManager ? [] : mutableFunctions,
      thoughtFieldName: this.thoughtFieldName,
      contextCache,
      ignoreBreakpoints: providerIgnoreBreakpoints,
      includeOptionalInputFieldsInSystemPrompt:
        options?.includeOptionalInputFieldsInSystemPrompt ??
        this.options?.includeOptionalInputFieldsInSystemPrompt,
      structuredOutputFunctionName: structuredOutputFunctionFallback
        ? STRUCTURED_OUTPUT_FUNCTION_NAME
        : undefined,
      customTemplate: options?.customTemplate ?? this.options?.customTemplate,
    });
    const instruction = this.getInstruction();
    if (instruction !== undefined && instruction.trim().length > 0) {
      promptTemplate.setInstruction(instruction);
    }
    const renderedPrompt:
      | AxRenderedPrompt
      | { chatPrompt: AxChatRequest['chatPrompt'] } =
      'renderWithMetrics' in promptTemplate &&
      typeof promptTemplate.renderWithMetrics === 'function'
        ? promptTemplate.renderWithMetrics(values as any, {
            examples: this.examples as any,
            demos: this.demos as any,
          })
        : {
            chatPrompt: promptTemplate.render(values as any, {
              examples: this.examples as any,
              demos: this.demos as any,
            }),
          };

    const prompt = renderedPrompt.chatPrompt;
    const renderedPromptMetrics =
      'promptMetrics' in renderedPrompt
        ? renderedPrompt.promptMetrics
        : undefined;

    const mem = options?.mem ?? this.options?.mem;
    if (!mem) {
      return {
        prompt,
        promptMetrics: renderedPromptMetrics ?? {
          systemPromptCharacters: countChatPromptContentChars(
            prompt.filter((msg) => msg.role === 'system')
          ),
          exampleChatContextCharacters: 0,
          mutableChatContextCharacters: countChatPromptContentChars(
            prompt.filter((msg) => msg.role !== 'system')
          ),
          chatContextCharacters: countChatPromptContentChars(
            prompt.filter((msg) => msg.role !== 'system')
          ),
          totalPromptCharacters: countChatPromptContentChars(prompt),
        },
      };
    }

    const selectedIndex = await selectFromSamplesInMemory(
      mem,
      options?.sessionId,
      {
        resultPicker: options?.resultPicker as
          | AxResultPickerFunction<OUT>
          | undefined,
      }
    );

    const memHistory = mem.history(selectedIndex, options?.sessionId);
    const memPrompt = [...memHistory, ...prompt];
    const memoryChars = countChatPromptContentChars(memHistory);
    return {
      prompt: memPrompt,
      promptMetrics:
        renderedPromptMetrics !== undefined
          ? {
              ...renderedPromptMetrics,
              mutableChatContextCharacters:
                renderedPromptMetrics.mutableChatContextCharacters +
                memoryChars,
              chatContextCharacters:
                renderedPromptMetrics.chatContextCharacters + memoryChars,
              totalPromptCharacters:
                renderedPromptMetrics.totalPromptCharacters + memoryChars,
            }
          : {
              systemPromptCharacters: countChatPromptContentChars(
                memPrompt.filter((msg) => msg.role === 'system')
              ),
              exampleChatContextCharacters: 0,
              mutableChatContextCharacters: countChatPromptContentChars(
                memPrompt.filter((msg) => msg.role !== 'system')
              ),
              chatContextCharacters: countChatPromptContentChars(
                memPrompt.filter((msg) => msg.role !== 'system')
              ),
              totalPromptCharacters: countChatPromptContentChars(memPrompt),
            },
    };
  }

  private async renderPromptForInternalUse(
    ai: Readonly<AxAIService>,
    values: IN,
    options?: Readonly<Partial<Omit<AxProgramForwardOptions<any>, 'functions'>>>
  ): Promise<AxChatRequest['chatPrompt']> {
    return (
      await this.renderPromptWithMetricsForInternalUse(ai, values, options)
    ).prompt;
  }

  /** @internal */
  public async _measurePromptCharsForInternalUse(
    ai: Readonly<AxAIService>,
    values: IN,
    options?: Readonly<Partial<Omit<AxProgramForwardOptions<any>, 'functions'>>>
  ): Promise<AxPromptMetrics> {
    const { promptMetrics } = await this.renderPromptWithMetricsForInternalUse(
      ai,
      values,
      options
    );
    return promptMetrics!;
  }

  private withConstructorDefaults<O extends object | undefined>(
    callOptions: O
  ): O {
    const defaults = this.options as Record<string, unknown> | undefined;
    if (!defaults) return callOptions;
    const call = (callOptions ?? {}) as Record<string, unknown>;
    let merged: Record<string, unknown> | undefined;
    for (const key of constructorDefaultOptionKeys) {
      if (call[key] === undefined && defaults[key] !== undefined) {
        merged ??= { ...call };
        merged[key] = defaults[key];
      }
    }
    for (const key of ['modelConfig', 'customLabels'] as const) {
      if (defaults[key] !== undefined) {
        merged ??= { ...call };
        merged[key] = {
          ...(defaults[key] as object),
          ...(call[key] as object | undefined),
        };
      }
    }
    return (merged ?? callOptions) as O;
  }

  private getSignatureName(): string {
    return this.signature.getDescription() || 'unknown_signature';
  }

  private getMetricsInstruments(
    meter?: Meter
  ): AxGenMetricsInstruments | undefined {
    return getOrCreateGenMetricsInstruments(meter);
  }

  private getEffectiveMeter(
    ai: Readonly<AxAIService>,
    options?: Readonly<Partial<AxProgramForwardOptions<any>>>
  ): Meter | undefined {
    const frame = axGetRuntimeHookFrame(options);
    const globalMeter = frame ? frame.globals.meter : axGlobals.meter;
    return (
      options?.meter ??
      this.options?.meter ??
      ai.getOptions().meter ??
      globalMeter
    );
  }

  // Helper to get merged custom labels from globals, AI service, and options
  private getMergedCustomLabels(
    ai?: Readonly<AxAIService>,
    options?: Readonly<{ customLabels?: Record<string, string> }>
  ): Record<string, string> {
    return mergeCustomLabels(
      axGlobals.customLabels,
      ai?.getOptions?.()?.customLabels,
      options?.customLabels
    );
  }

  public updateMeter(meter?: Meter): void {
    // This now just updates the global singleton, no need to store locally
    getOrCreateGenMetricsInstruments(meter);
  }

  private createStates(n: number) {
    return Array.from({ length: n }, (_, index) => ({
      index,
      functionCalls: [],
      values: {},
      content: '',
      functionsExecuted: new Set<string>(),
      xstate: {
        extractedFields: [],
        streamedIndex: {},
        s: -1,
      },
    }));
  }

  public addAssert(fn: AxAssertion<OUT>['fn'], message?: string) {
    this.asserts.push({ fn, message });
  }

  public addStreamingAssert(
    fieldName: keyof OUT,
    fn: AxStreamingAssertion['fn'],
    message?: string
  ) {
    // Validate that the field name exists in the output signature
    const outputField = this.signature
      .getOutputFields()
      .find((f) => f.name === fieldName);

    if (!outputField) {
      throw new Error(
        `addStreamingAssert: field ${String(fieldName)} not found in output signature`
      );
    }

    // Ensure the field is a string field for streaming assertions
    const ft = outputField.type?.name;
    const isStringField = !ft || ft === 'string' || ft === 'code';

    if (!isStringField) {
      throw new Error(
        `addStreamingAssert: field ${String(fieldName)} must be a string field for streaming assertions`
      );
    }

    this.streamingAsserts.push({ fieldName: String(fieldName), fn, message });
  }

  private addFieldProcessorInternal(
    fieldName: string,
    fn: AxFieldProcessor['process'],
    streaming = false
  ) {
    const field = this.signature
      .getOutputFields()
      .find((f) => f.name === fieldName);

    if (!field) {
      throw new Error(`addFieldProcessor: field ${fieldName} not found`);
    }

    if (streaming) {
      const ft = field.type?.name;
      const isText = !ft || ft === 'string' || ft === 'code';

      if (!isText) {
        throw new Error(
          `addFieldProcessor: field ${fieldName} must be a text field`
        );
      }
      this.streamingFieldProcessors.push({ field, process: fn });
    } else {
      this.fieldProcessors.push({ field, process: fn });
    }
  }

  public addStreamingFieldProcessor<K extends keyof OUT>(
    fieldName: K,
    fn: (
      value: string,
      context?: { values?: OUT; sessionId?: string; done?: boolean }
    ) => unknown | Promise<unknown>
  ) {
    this.addFieldProcessorInternal(
      String(fieldName),
      fn as AxFieldProcessor['process'],
      true
    );
  }

  public addFieldProcessor<K extends keyof OUT>(
    fieldName: K,
    fn: (
      value: OUT[K],
      context?: { values?: OUT; sessionId?: string; done?: boolean }
    ) => unknown | Promise<unknown>
  ) {
    this.addFieldProcessorInternal(
      String(fieldName),
      fn as AxFieldProcessor['process'],
      false
    );
  }

  /**
   * Get the normalized chat log from the last forward() call.
   * One entry per AI chat round-trip, with roles normalized to
   * system/user/assistant/tool and inline XML for thinking and tool calls.
   */
  public getChatLog(): readonly AxChatLogEntry[] {
    return this.chatLog;
  }

  private captureChatResponseLogMetadata(
    metadata: AxChatResponseLogMetadata,
    res: Readonly<AxChatResponse>
  ): void {
    metadata.sessionId = res.sessionId ?? metadata.sessionId;
    metadata.remoteId = res.remoteId ?? metadata.remoteId;
    metadata.remoteRequestId = res.remoteRequestId ?? metadata.remoteRequestId;
    metadata.remoteSessionId = res.remoteSessionId ?? metadata.remoteSessionId;
    if (res.providerMetadata) {
      metadata.providerMetadata = {
        ...(metadata.providerMetadata ?? {}),
      };
      for (const [provider, providerMetadata] of Object.entries(
        res.providerMetadata
      )) {
        metadata.providerMetadata[provider] = {
          ...(metadata.providerMetadata[provider] ?? {}),
          ...providerMetadata,
        };
      }
    }
  }

  private applyChatResponseLogMetadata(
    entry: AxChatLogEntry,
    metadata: Readonly<AxChatResponseLogMetadata>
  ): void {
    if (metadata.sessionId) entry.sessionId = metadata.sessionId;
    if (metadata.remoteId) entry.remoteId = metadata.remoteId;
    if (metadata.remoteRequestId) {
      entry.remoteRequestId = metadata.remoteRequestId;
    }
    if (metadata.remoteSessionId) {
      entry.remoteSessionId = metadata.remoteSessionId;
    }
    if (metadata.providerMetadata) {
      entry.providerMetadata = metadata.providerMetadata;
    }
  }

  /**
   * Normalize internal chatPrompt messages to standard training format.
   * If functions are provided, appends a <tools> JSON block to the system prompt.
   */
  private normalizeChatMessages(
    chatPrompt: AxChatRequest['chatPrompt'],
    functions?: AxFunction[]
  ): AxChatLogMessage[] {
    const messages: AxChatLogMessage[] = [];

    const toolsBlock =
      functions && functions.length > 0
        ? `\n<tools>\n${JSON.stringify(
            functions.map((fn) => ({
              type: 'function',
              function: {
                name: fn.name,
                description: fn.description,
                ...(fn.parameters ? { parameters: fn.parameters } : {}),
              },
            }))
          )}\n</tools>`
        : '';

    for (const msg of chatPrompt) {
      switch (msg.role) {
        case 'system':
          messages.push({
            role: 'system',
            content: msg.content + toolsBlock,
          });
          break;

        case 'user': {
          let content: string;
          if (typeof msg.content === 'string') {
            content = msg.content;
          } else {
            content = msg.content
              .map((part) => {
                switch (part.type) {
                  case 'text':
                    return part.text;
                  case 'image':
                    return '[image]';
                  case 'audio':
                    return '[audio]';
                  case 'file':
                    return '[file]';
                  case 'url':
                    return '[url]';
                  default:
                    return '';
                }
              })
              .join('\n');
          }
          messages.push({ role: 'user', content });
          break;
        }

        case 'assistant': {
          let content = '';
          if (msg.thought) {
            content += `<think>${msg.thought}</think>\n`;
          }
          if (msg.content) {
            content += msg.content;
          }
          if (msg.functionCalls?.length) {
            for (const fc of msg.functionCalls) {
              const callObj = {
                name: fc.function.name,
                arguments: fc.function.params ?? {},
              };
              content += `\n<tool_call>\n${JSON.stringify(callObj)}\n</tool_call>`;
            }
          }
          messages.push({ role: 'assistant', content: content.trim() });
          break;
        }

        case 'function': {
          // Look up function name from preceding assistant message's functionCalls
          let name = 'unknown';
          for (let i = messages.length - 1; i >= 0; i--) {
            const prev = chatPrompt[i];
            if (prev && prev.role === 'assistant' && prev.functionCalls) {
              const match = prev.functionCalls.find(
                (fc) => fc.id === msg.functionId
              );
              if (match) {
                name = match.function.name;
                break;
              }
            }
          }
          messages.push({ role: 'tool', name, content: msg.result });
          break;
        }
      }
    }

    return messages;
  }

  /**
   * Build an assistant AxChatLogMessage from a response result.
   */
  private buildAssistantLogMessage(
    result: Readonly<AxChatResponseResult>
  ): AxChatLogMessage {
    let content = '';
    if (result.thought) {
      content += `<think>${result.thought}</think>\n`;
    }
    if (result.content) {
      content += result.content;
    }
    if (result.functionCalls?.length) {
      for (const fc of result.functionCalls) {
        const callObj = {
          name: fc.function.name,
          arguments: fc.function.params ?? {},
        };
        content += `\n<tool_call>\n${JSON.stringify(callObj)}\n</tool_call>`;
      }
    }
    return { role: 'assistant' as const, content: content.trim() };
  }

  private async forwardSendRequest({
    ai,
    values,
    mem,
    options,
    traceContext,
    functions,
    functionCall,
    stepIndex,
    preRenderedPrompt,
    sessionChat,
  }: Readonly<{
    sessionChat?: (
      req: AxChatRequest,
      options: AxAIServiceOptions
    ) => Promise<AxChatResponse>;
    ai: Readonly<AxAIService>;
    values: IN;
    mem: AxAIMemory;
    options?: Omit<AxProgramForwardOptions<any>, 'ai' | 'mem'>;
    traceContext?: Context;
    functions: AxFunction[];
    functionCall: AxChatRequest['functionCall'] | undefined;
    stepIndex?: number;
    preRenderedPrompt?: {
      prompt: AxChatRequest['chatPrompt'];
      promptMetrics?: AxPromptMetrics;
    };
  }>) {
    const {
      sessionId,
      promptCacheKey,
      model,
      rateLimiter,
      stream,
      thinkingTokenBudget,
      showThoughts,
    } = options ?? {};

    // Use selectFromSamplesInMemory to choose the best sample before getting history
    const selectedIndex = await selectFromSamplesInMemory(mem, sessionId, {
      resultPicker: options?.resultPicker as
        | AxResultPickerFunction<OUT>
        | undefined,
    });

    const { prompt: basePrompt, promptMetrics: basePromptMetrics } =
      preRenderedPrompt ??
      (await this.renderPromptWithMetricsForInternalUse(
        ai,
        values as any,
        {
          ...options,
          sessionId,
        },
        functions
      ));
    const chatPrompt = mem?.history(selectedIndex, sessionId) ?? basePrompt;

    // Replace the system message in memory history with the freshly rendered one.
    // This ensures that dynamically added functions (via ctx.addFunctions()) are
    // reflected in the <available_functions> section of the system prompt, not just
    // in the API tool definitions.
    if (
      chatPrompt !== basePrompt &&
      chatPrompt.length > 0 &&
      basePrompt.length > 0
    ) {
      const freshSystemMsg = basePrompt.find((m) => m.role === 'system');
      if (freshSystemMsg) {
        const systemIdx = chatPrompt.findIndex((m) => m.role === 'system');
        if (systemIdx !== -1) {
          chatPrompt[systemIdx] = freshSystemMsg;
        }
      }
    }

    // History transformation for prompt-mode is handled centrally in base.ts

    if (chatPrompt.length === 0) {
      throw new Error('No chat prompt found');
    }
    const modelConfig = {
      ...options?.modelConfig,
      ...(options?.sampleCount ? { n: options.sampleCount } : {}),
      ...(options?.sampleCount && options?.modelConfig?.temperature === 1
        ? { temperature: 0.8 }
        : {}),
    };

    const debug = this.isDebug(ai, options);
    const firstStep = stepIndex === 0;
    const logger = this.getLogger(ai, options);
    const debugPromptMetrics = debug
      ? (() => {
          if (!basePromptMetrics) {
            const chatContextCharacters = countChatPromptContentChars(
              chatPrompt.filter((msg) => msg.role !== 'system')
            );
            return {
              systemPromptCharacters: countChatPromptContentChars(
                chatPrompt.filter((msg) => msg.role === 'system')
              ),
              exampleChatContextCharacters: 0,
              mutableChatContextCharacters: chatContextCharacters,
              chatContextCharacters,
              totalPromptCharacters: countChatPromptContentChars(chatPrompt),
            };
          }

          const extraHistoryMessages = chatPrompt.slice(basePrompt.length);
          const extraHistoryCharacters =
            countChatPromptContentChars(extraHistoryMessages);
          const mutableChatContextCharacters =
            basePromptMetrics.mutableChatContextCharacters +
            extraHistoryCharacters;
          const chatContextCharacters =
            basePromptMetrics.exampleChatContextCharacters +
            mutableChatContextCharacters;

          return {
            systemPromptCharacters: basePromptMetrics.systemPromptCharacters,
            exampleChatContextCharacters:
              basePromptMetrics.exampleChatContextCharacters,
            mutableChatContextCharacters,
            chatContextCharacters,
            totalPromptCharacters:
              basePromptMetrics.systemPromptCharacters + chatContextCharacters,
          };
        })()
      : undefined;

    const responseMetadata: AxChatResponseLogMetadata = this
      .structuredOutputRung
      ? {
          providerMetadata: {
            ax: {
              structured_output_rung: this.structuredOutputRung,
            },
          },
        }
      : {};

    // Save original functions for chat log before zeroing out for prompt mode.
    // Filter out internal structured-output functions.
    const logFunctions = functions.filter(
      (fn) => !isReservedStructuredOutputFunctionName(fn.name)
    );

    // The legacy alias remains available to the response processor for stored
    // trajectories, but it is never advertised to providers.
    const providerFunctions = functions.filter(
      (fn) => fn.name !== LEGACY_STRUCTURED_OUTPUT_FUNCTION_NAME
    );

    // Do not send native functions to the provider when emulating via prompt mode
    functions = this.signatureToolCallingManager ? [] : providerFunctions;

    let responseFormat: AxChatRequest['responseFormat'];

    const outputFields = this.signature.getOutputFields();
    if (this.structuredOutputRung === 'native') {
      // Use the signature-to-schema converter we implemented
      const schema = toJsonSchema(outputFields, 'Schema', {
        flexibleJsonFieldsAsString: true,
        strictStructuredOutputs: true,
      });
      const fieldDescriptions = outputValueDescriptions(outputFields);

      responseFormat = {
        type: 'json_schema',
        ...(fieldDescriptions ? { fieldDescriptions } : {}),
        schema: {
          name: 'output',
          strict: true,
          schema,
        },
      };
    } else if (this.structuredOutputRung === 'json_object') {
      responseFormat = { type: 'json_object' };
    }

    // Mark last function for caching (creates breakpoint after tools)
    // Cache if cacheBreakpoint is 'after-functions' or 'after-examples', or ignoreBreakpoints is true
    const contextCache = this.getEffectiveContextCache(ai, options);
    const cacheBreakpoint = contextCache?.cacheBreakpoint ?? 'after-examples';
    const ignoreBreakpoints =
      ai.getFeatures?.(model)?.caching?.cacheBreakpoints === false;
    const shouldAlwaysCacheGeminiTools =
      Boolean(contextCache) &&
      ai.getName() === 'GoogleGeminiAI' &&
      ai.getFeatures?.(model)?.caching?.supported === true;
    const shouldCacheFunctions =
      contextCache &&
      (shouldAlwaysCacheGeminiTools ||
        ignoreBreakpoints ||
        cacheBreakpoint === 'after-functions' ||
        cacheBreakpoint === 'after-examples');
    const functionsWithCache =
      functions?.length && shouldCacheFunctions
        ? functions.map((fn, i) => ({
            ...fn,
            cache: i === functions.length - 1,
          }))
        : functions;

    let res = await (sessionChat ?? ai.chat.bind(ai))(
      {
        chatPrompt,
        // Do not send native functions to the provider when emulating via prompt mode
        functions: functionsWithCache,
        functionCall,
        modelConfig,
        model,
        responseFormat,
      },
      {
        sessionId,
        executionPath: options?.executionPath,
        webSocket: options?.webSocket ?? ai.getOptions().webSocket,
        fetch: options?.fetch ?? ai.getOptions().fetch,
        timeout: options?.timeout ?? ai.getOptions().timeout,
        asyncMode: options?.asyncMode,
        promptCacheKey: promptCacheKey ?? this.options?.promptCacheKey,
        functionCallSource:
          typeof functionCall === 'object' &&
          functionCall.function.name === STRUCTURED_OUTPUT_FUNCTION_NAME
            ? 'ax'
            : functionCall
              ? 'caller'
              : undefined,
        rateLimiter,
        tracer: options?.tracer,
        meter: options?.meter,
        [axRuntimeHookFrame]: axGetRuntimeHookFrame(options),
        stream,
        debug,
        // Hide system prompt in debug logging for steps > 0 to reduce noise in multi-step workflows
        debugHideSystemPrompt:
          options?.debugHideSystemPrompt ??
          this.options?.debugHideSystemPrompt ??
          !firstStep,
        thinkingTokenBudget,
        showThoughts,
        useExpensiveModel:
          options?.useExpensiveModel ?? this.options?.useExpensiveModel,
        traceContext,
        abortSignal: mergeAbortSignals(
          options?.abortSignal,
          axGlobals.abortSignal
        ),
        stepIndex,
        logger,
        functionCallMode:
          options?.functionCallMode ?? this.options?.functionCallMode ?? 'auto',
        contextCache,
        retry: options?.retry ?? this.options?.retry,
        customLabels: options?.customLabels,
        serviceTier: options?.serviceTier,
        verbose: options?.verbose,
        beta: options?.beta,
        corsProxy: options?.corsProxy,
        includeRequestBodyInErrors: options?.includeRequestBodyInErrors,
        promptCacheRetention: options?.promptCacheRetention,
        excludeContentFromTrace: options?.excludeContentFromTrace,
        usageContext: axMergeUsageContexts(
          this.options?.usageContext,
          options?.usageContext
        ),
      } as AxAIServiceOptions & AxRuntimeHookFramedOptions
    );

    if (sessionChat && stream && !(res instanceof ReadableStream)) {
      const finalResponse = res;
      res = new ReadableStream<AxChatResponse>({
        start(controller) {
          controller.enqueue(finalResponse);
          controller.close();
        },
      });
    }

    if (res instanceof ReadableStream) {
      const source = res;
      res = source.pipeThrough(
        new TransformStream<AxChatResponse, AxChatResponse>({
          transform: (chunk, controller) => {
            this.captureChatResponseLogMetadata(responseMetadata, chunk);
            controller.enqueue(chunk);
          },
        })
      );
    } else {
      this.captureChatResponseLogMetadata(responseMetadata, res);
    }

    // Capture chat log entry
    const logMessages = this.normalizeChatMessages(chatPrompt, logFunctions);
    const modelStr = String(model ?? ai.getLastUsedChatModel?.() ?? '');

    if (!(res instanceof ReadableStream)) {
      // Non-streaming: capture full response immediately
      for (const result of res.results) {
        logMessages.push(this.buildAssistantLogMessage(result));
      }
      this.chatLog.push({
        model: modelStr,
        messages: logMessages,
        ...responseMetadata,
        modelUsage: res.modelUsage,
      });
    } else {
      // Streaming: push partial entry, response filled after stream completes
      this.chatLog.push({
        model: modelStr,
        messages: logMessages,
        ...responseMetadata,
      });
    }

    return { res, debugPromptMetrics, responseMetadata };
  }

  private async *forwardCore({
    ai,
    values,
    mem,
    options,
    stepIndex,
    span,
    traceContext,
    states,
    stopFunctionNames,
    stepContext,
    preRenderedPrompt,
  }: Readonly<{
    ai: Readonly<AxAIService>;
    values: IN;
    mem: AxAIMemory;
    options: Omit<AxProgramForwardOptions<any>, 'ai' | 'mem'>;
    stepIndex?: number;
    span?: Span;
    traceContext?: Context;
    states: InternalAxGenState[];
    stopFunctionNames?: readonly string[];
    stepContext?: AxStepContextImpl;
    preRenderedPrompt?: {
      prompt: AxChatRequest['chatPrompt'];
      promptMetrics?: AxPromptMetrics;
    };
  }>): AsyncGenDeltaOut<OUT> {
    const { sessionId, functions: functionList } = options ?? {};

    const functionResultFormatter =
      options?.functionResultFormatter ?? this.options?.functionResultFormatter;

    const definedFunctionCall =
      options?.functionCall ?? this.options?.functionCall;

    const signatureToolCallingManager = this.signatureToolCallingManager;

    const strictMode = options?.strictMode ?? this.options?.strictMode ?? false;
    const model = options.model;
    const usage = this.usage;
    const firstStep = stepIndex === 0;

    const debug = this.isDebug(ai, options);
    const logger = this.getLogger(ai, options);
    const activeTracer =
      options?.tracer ??
      this.options?.tracer ??
      ai.getOptions().tracer ??
      axGlobals.tracer;

    // A forced call applies to the first step only. The structured-output
    // functions stay declared after it, since the model answers through them.
    let { functions, functionCall } = createFunctionConfig(
      functionList,
      definedFunctionCall,
      firstStep,
      (fn) => isReservedStructuredOutputFunctionName(fn.name)
    );

    // When using function-call fallback for structured output,
    // force the AI to call the structured output function
    // only when there are no user-defined functions.
    if (this.structuredOutputFunctionFallback) {
      const userFunctionCount = functions.filter(
        (f) => !isReservedStructuredOutputFunctionName(f.name)
      ).length;
      if (userFunctionCount === 0) {
        functionCall = {
          type: 'function',
          function: { name: STRUCTURED_OUTPUT_FUNCTION_NAME },
        };
      } else if (
        functionCall === 'required' ||
        (typeof functionCall === 'object' &&
          !isReservedStructuredOutputFunctionName(functionCall.function.name))
      ) {
        // A caller-forced call must reach a user function, so the output
        // function is offered only once the forcing is dropped.
        functions = functions.filter(
          (f) => !isReservedStructuredOutputFunctionName(f.name)
        );
      }
    }

    functions = functions.map((fn) =>
      stopFunctionNames?.includes(fn.name.toLowerCase()) ||
      isReservedStructuredOutputFunctionName(fn.name)
        ? { ...fn, execution: 'blocking' }
        : fn
    );

    // A chat session's response streams through the same extraction as a
    // plain stream, on its own state, so a delta never has to take back text
    // it already sent (a label split across partial events, say). A new
    // response starts a new version; the final pass below adds what the
    // partials did not cover.
    let sessionVersion = -1;
    let sessionResponseId: string | undefined;
    let sessionState: InternalAxGenState | undefined;
    let provisionalValues: Record<string, unknown> = {};
    const provisional: (DeltaOut<OUT> & {
      [axSessionOutputVersion]: number;
    })[] = [];
    let wakeProvisional: (() => void) | undefined;
    let requestSettled = false;
    const selectResponse = (responseId: string) => {
      if (responseId === sessionResponseId) return;
      sessionResponseId = responseId;
      sessionVersion++;
      sessionState = this.createStates(1)[0];
      provisionalValues = {};
    };
    const onSessionDelta = async (
      responseId: string,
      response: AxChatResponse
    ) => {
      selectResponse(responseId);
      const state = sessionState;
      if (!options.stream || !state) return;
      for (const result of response.results) {
        if (result.index !== 0) continue;
        try {
          for await (const { delta } of processSessionPartialResult<OUT>({
            result,
            state,
            sessionId,
            signature: this.signature,
            strictMode,
            skipEarlyFail:
              (ai.getFeatures().functionCot ?? false) && functions.length > 0,
            thoughtFieldName: this.thoughtFieldName,
            streamingAsserts: this.streamingAsserts,
            parseJsonStringFields:
              this.signature.hasComplexFields() &&
              !this.structuredOutputFunctionFallback,
            strictStructuredJson:
              this.structuredOutputRung === 'json_object' ||
              (this.structuredOutputRung === 'native' &&
                !this.signature.hasComplexFields()),
          })) {
            mergeDeltaValues(provisionalValues, delta);
            provisional.push({
              index: 0,
              delta,
              [axSessionOutputVersion]: sessionVersion,
            });
            wakeProvisional?.();
          }
        } catch (error) {
          if (!(error instanceof ValidationError)) {
            // As in a plain stream, the retry's prompt keeps the answer its
            // correction refers to.
            if (error instanceof AxStreamingAssertionError)
              mem.addResponse(
                [{ index: 0, content: state.content }],
                sessionId
              );
            throw error;
          }
          // Incomplete field values are validated when the response
          // finishes; until then this response streams no more.
          sessionState = undefined;
          return;
        }
      }
    };
    const requestPromise = this.forwardSendRequest({
      ai,
      values,
      mem,
      options,
      traceContext,
      functions,
      functionCall,
      stepIndex,
      preRenderedPrompt,
      sessionChat:
        (options.asyncMode ?? ai.getOptions().asyncMode) !== 'off' &&
        ai.openChatSession &&
        ai.getFeatures(model).asyncTools &&
        options.functionCallMode !== 'prompt' &&
        (functions.some((fn) => fn.execution === 'background') ||
          options.control)
          ? (request, serviceOptions) =>
              axRunChatSession({
                ai,
                request,
                onDelta: onSessionDelta,
                onResponse: selectResponse,
                options: {
                  ...serviceOptions,
                  control: options.control,
                  executionPath: options.executionPath,
                },
                functions,
                finalFunctions: functions
                  .filter(
                    (fn) =>
                      stopFunctionNames?.includes(fn.name.toLowerCase()) ||
                      isReservedStructuredOutputFunctionName(fn.name)
                  )
                  .map((fn) => fn.name),
                mem,
                maxResponses:
                  (stepContext?.maxSteps ?? options.maxSteps ?? 25) -
                  (stepIndex ?? 0),
                recordUsage: (response) => {
                  if (response.modelUsage) usage.push(response.modelUsage);
                },
                execute: async (call) => {
                  const captured: import('../ai/types.js').AxFunctionResult[] =
                    [];
                  let executed: Set<string>;
                  try {
                    executed = await processFunctions({
                      ai,
                      functionList: functions.map((fn) => ({
                        ...fn,
                        parameters: fn.parameters ?? {
                          type: 'object',
                          properties: {},
                        },
                        func: (args, extra) => {
                          axValidateToolArguments(fn.parameters, args);
                          options.control?.emit({
                            type: 'tool.started',
                            path: options.executionPath ?? 'root',
                            callId: call.id,
                          });
                          return fn.parameters
                            ? fn.func.length === 2
                              ? fn.func(args, extra)
                              : fn.func(args)
                            : fn.func.length === 1
                              ? fn.func(extra)
                              : fn.func();
                        },
                      })),
                      functionCalls: [
                        {
                          id: call.id,
                          name: call.function.name,
                          args:
                            typeof call.function.params === 'string'
                              ? call.function.params
                              : JSON.stringify(call.function.params ?? {}),
                        },
                      ],
                      mem: {
                        addFunctionResults: (
                          results: import('../ai/types.js').AxFunctionResult[]
                        ) => captured.push(...results),
                      },
                      sessionId,
                      traceId: span
                        ? (span as any).spanContext?.().traceId
                        : undefined,
                      traceContext,
                      tracer: activeTracer,
                      span,
                      index: 0,
                      functionResultFormatter,
                      logger,
                      debug,
                      stopFunctionNames,
                      step: stepContext,
                      abortSignal: options.abortSignal,
                      onFunctionCall: options.onFunctionCall,
                      mcpExecutionContext: options._mcpExecutionContext,
                      eventContext: options.eventContext,
                      control: options.control,
                      executionPath: options.executionPath,
                    });
                  } catch (error) {
                    if (error instanceof AxStopFunctionCallException)
                      mem.addFunctionResults(captured, sessionId);
                    throw error;
                  }
                  for (const name of executed)
                    states[0]?.functionsExecuted.add(name);
                  return captured;
                },
              })
          : undefined,
    });
    void requestPromise.then(
      () => {
        requestSettled = true;
        wakeProvisional?.();
      },
      () => {
        requestSettled = true;
        wakeProvisional?.();
      }
    );
    while (!requestSettled || provisional.length) {
      if (provisional.length) yield provisional.shift()!;
      else
        await new Promise<void>((resolve) => {
          wakeProvisional = resolve;
        });
    }
    const { res, debugPromptMetrics, responseMetadata } = await requestPromise;
    const signature = this.signature;
    const thoughtFieldName = this.thoughtFieldName;
    const finalValues: Record<string, unknown> = {};
    const versioned = async function* (
      outputs: AsyncGenDeltaOut<OUT>
    ): AsyncGenDeltaOut<OUT> {
      for await (const output of outputs) {
        if (sessionVersion < 0 || !options.stream) {
          yield output;
          continue;
        }
        mergeDeltaValues(finalValues, output.delta);
        if (
          Object.entries(finalValues).some(
            ([key, value]) =>
              typeof value === 'string' &&
              typeof provisionalValues[key] === 'string' &&
              !value.startsWith(provisionalValues[key] as string)
          )
        ) {
          sessionVersion++;
          provisionalValues = {};
        }
        const { delta, fullValues } = createStructuredDelta<OUT>({
          signature,
          parsedValues: finalValues,
          previousValues: provisionalValues,
          partialMarker: null,
        });
        // As in a plain stream, the thought comes through too: whatever the
        // partials did not already send.
        const thought = finalValues[thoughtFieldName];
        if (typeof thought === 'string') {
          const sent = provisionalValues[thoughtFieldName];
          const rest = thought.slice(
            typeof sent === 'string' ? sent.length : 0
          );
          if (rest) (delta as Record<string, unknown>)[thoughtFieldName] = rest;
          fullValues[thoughtFieldName] = thought;
        }
        // The consumer keeps the fields this pass has not reached yet.
        provisionalValues = { ...provisionalValues, ...fullValues };
        if (Object.keys(delta).length)
          yield Object.assign(
            { ...output, delta },
            { [axSessionOutputVersion]: sessionVersion }
          );
      }
    };

    if (res instanceof ReadableStream) {
      yield* versioned(
        processStreamingResponse<OUT>({
          ai,
          model,
          res,
          mem,
          sessionId,
          traceId: span ? (span as any).spanContext?.().traceId : undefined,
          traceContext,
          tracer: activeTracer,
          functions,
          strictMode,
          span,
          states,
          usage,
          streamingAsserts: this.streamingAsserts,
          asserts: this.asserts,
          fieldProcessors: this.fieldProcessors,
          streamingFieldProcessors: this.streamingFieldProcessors,
          thoughtFieldName: this.thoughtFieldName,
          excludeContentFromTrace:
            options.excludeContentFromTrace ?? this.excludeContentFromTrace,
          signature: this.signature,
          parseJsonStringFields:
            this.signature.hasComplexFields() &&
            !this.structuredOutputFunctionFallback,
          strictStructuredJson:
            this.structuredOutputRung === 'json_object' ||
            (this.structuredOutputRung === 'native' &&
              !this.signature.hasComplexFields()),
          logger,
          debugPromptMetrics,
          onFunctionCall: options.onFunctionCall,
          mcpExecutionContext: options._mcpExecutionContext,
          eventContext: options.eventContext,
          control: options.control,
          executionPath: options.executionPath,
          debug,
          functionResultFormatter,
          signatureToolCallingManager,
          stopFunctionNames,
          disableMemoryCleanup: options.disableMemoryCleanup,
          stepContext,
          abortSignal: options.abortSignal,
        })
      );

      // Update the streaming chat log entry with accumulated response.
      // For streaming, state.content is the raw LLM output. We don't extract
      // thought separately here because state.values[thoughtFieldName] is a DSP
      // output field (not native extended thinking) and would be duplicated.
      const lastLogEntry = this.chatLog[this.chatLog.length - 1];
      if (lastLogEntry) {
        this.applyChatResponseLogMetadata(lastLogEntry, responseMetadata);
        for (const state of states) {
          lastLogEntry.messages.push(
            this.buildAssistantLogMessage({
              index: state.index,
              content: state.content || undefined,
              functionCalls:
                state.functionCalls.length > 0
                  ? state.functionCalls
                  : undefined,
            })
          );
        }
        // Attach usage if available
        if (this.usage.length > 0) {
          lastLogEntry.modelUsage = this.usage[this.usage.length - 1];
        }
      }
    } else {
      yield* versioned(
        processResponse<OUT>({
          ai,
          model,
          res,
          mem,
          sessionId,
          traceId: span ? (span as any).spanContext?.().traceId : undefined,
          traceContext,
          tracer: activeTracer,
          functions,
          span,
          strictMode,
          states,
          usage,
          asserts: this.asserts,
          fieldProcessors: this.fieldProcessors,
          thoughtFieldName: this.thoughtFieldName,
          excludeContentFromTrace:
            options.excludeContentFromTrace ?? this.excludeContentFromTrace,
          signature: this.signature,
          parseJsonStringFields:
            this.signature.hasComplexFields() &&
            !this.structuredOutputFunctionFallback,
          strictStructuredJson:
            this.structuredOutputRung === 'json_object' ||
            (this.structuredOutputRung === 'native' &&
              !this.signature.hasComplexFields()),
          logger,
          debugPromptMetrics,
          onFunctionCall: options.onFunctionCall,
          mcpExecutionContext: options._mcpExecutionContext,
          eventContext: options.eventContext,
          control: options.control,
          executionPath: options.executionPath,
          debug,
          functionResultFormatter,
          signatureToolCallingManager,
          stopFunctionNames,
          disableMemoryCleanup: options.disableMemoryCleanup,
          stepContext,
          abortSignal: options.abortSignal,
        })
      );
    }
  }

  private async *_forward2(
    ai: Readonly<AxAIService>,
    values: IN,
    states: InternalAxGenState[],
    options: Readonly<AxProgramForwardOptions<any>>,
    span?: Span,
    traceContext?: Context
  ): AxGenStreamingOut<OUT> {
    // Reset per-call state so previous calls do not leak.
    this.signatureToolCallingManager = undefined;
    this.structuredOutputFunctionFallback = false;
    this.structuredOutputRung = undefined;
    this.chatLog = [];

    const rawStop = options?.stopFunction ?? this.options?.stopFunction;
    let stopFunctionNames = Array.isArray(rawStop)
      ? rawStop.map((s) => s.toLowerCase())
      : rawStop
        ? [rawStop.toLowerCase()]
        : undefined;

    const maxRetries = options.maxRetries ?? this.options?.maxRetries ?? 3;
    const maxSteps = options.maxSteps ?? this.options?.maxSteps ?? 25;

    const mem = options.mem ?? this.options?.mem ?? new AxMemory();

    const mutableFunctions = options.functions
      ? parseFunctions(options.functions)
      : [...this.functions];
    assertNoReservedStructuredOutputFunctions(mutableFunctions);
    let mcpCatalogRevision = options._mcpExecutionContext?.getCatalogRevision();

    // Create step context for programmatic loop control
    const stepContext = new AxStepContextImpl(maxSteps);

    // Inject self-tuning function if enabled
    let selfTuningConfig: AxSelfTuningConfig | undefined;

    if (options.selfTuning) {
      selfTuningConfig =
        options.selfTuning === true
          ? { model: true, thinkingBudget: true }
          : options.selfTuning;

      // Validate: model selection requires a models list with 2+ chat models
      if (selfTuningConfig.model !== false) {
        const modelList = ai.getModelList();
        const chatModels = modelList?.filter((entry) => 'model' in entry);
        if (!chatModels || chatModels.length < 2) {
          throw new Error(
            'Self-tuning with model selection requires the AI service to have a `models` list with at least 2 chat models. ' +
              'Either configure models on your AI service or disable model selection with `selfTuning: { model: false }`.'
          );
        }
      }

      const tuningFn = createSelfTuningFunction(
        ai,
        selfTuningConfig,
        options.model ? String(options.model) : undefined
      );
      mutableFunctions.push(tuningFn);
    }

    // Mutable options that can be changed by step context mutations
    let mutableOptions = { ...options };

    const stepHooks = options.stepHooks;

    const hasFunctions = mutableFunctions && mutableFunctions.length > 0;

    const functionCallMode =
      options.functionCallMode ?? this.options?.functionCallMode ?? 'auto';

    if (
      hasFunctions &&
      ai.getFeatures(options.model).functionEmulation === false &&
      !ai.getFeatures(options.model).functions
    ) {
      throw new Error(
        `${ai.getName()} does not support tools or prompt tool emulation`
      );
    }

    // Handle prompt mode
    if (hasFunctions && functionCallMode === 'prompt') {
      this.signatureToolCallingManager = new SignatureToolCallingManager(
        mutableFunctions
      );
    }

    // Handle auto mode
    if (
      hasFunctions &&
      functionCallMode === 'auto' &&
      !ai.getFeatures(options.model).functions
    )
      this.signatureToolCallingManager = new SignatureToolCallingManager(
        mutableFunctions
      );

    let err:
      | ValidationError
      | AxAssertionError
      | AxStreamingAssertionError
      | undefined;
    let lastError: Error | undefined;
    // What the failing attempt answered, for the exhausted-retries message.
    let lastOutput = '';

    const promptTemplateClass =
      this.options?.promptTemplate ?? AxPromptTemplate;

    // Use SignatureToolCallingManager to process signature if in prompt mode
    if (this.signatureToolCallingManager) {
      this.signature = this.signatureToolCallingManager.processSignature(
        this.signature
      );
      this.setSignature(this.signature);
    }

    const features = ai.getFeatures?.(options.model);
    const structuredOutputMode =
      options.structuredOutputMode ??
      this.options?.structuredOutputMode ??
      'auto';

    this.structuredOutputRung = selectStructuredOutputRung(
      this.signature,
      features,
      structuredOutputMode,
      `${ai.getName()} (${String(options.model ?? 'default model')})`
    );
    this.structuredOutputFunctionFallback =
      this.structuredOutputRung === 'function';

    // When fallback is active, create synthetic function and add to stop functions
    if (this.structuredOutputFunctionFallback) {
      const parameters = toJsonSchema(this.signature.getOutputFields());
      const syntheticFunction: AxFunction = {
        name: STRUCTURED_OUTPUT_FUNCTION_NAME,
        description:
          'Emit the complete structured program output using the declared argument shape.',
        parameters,
        func: async () => 'done',
      };
      const legacyReplayFunction: AxFunction = {
        ...syntheticFunction,
        name: LEGACY_STRUCTURED_OUTPUT_FUNCTION_NAME,
      };
      mutableFunctions.push(syntheticFunction, legacyReplayFunction);

      stopFunctionNames = [
        ...(stopFunctionNames ?? []),
        STRUCTURED_OUTPUT_FUNCTION_NAME.toLowerCase(),
        LEGACY_STRUCTURED_OUTPUT_FUNCTION_NAME.toLowerCase(),
      ];
    }

    // Check if provider has automatic cache lookback (e.g., Anthropic)
    const providerIgnoreBreakpoints =
      ai.getFeatures?.(options.model)?.caching?.cacheBreakpoints === false;
    const contextCache = this.getEffectiveContextCache(ai, options);

    const currentPromptTemplateOptions = {
      structuredOutput: this.structuredOutputRung !== undefined,
      // Prefer per-call functions; fall back to parsed functions from constructor
      functions: this.signatureToolCallingManager
        ? []
        : mutableFunctions.filter(
            (fn) => fn.name !== LEGACY_STRUCTURED_OUTPUT_FUNCTION_NAME
          ),
      thoughtFieldName: this.thoughtFieldName,
      contextCache, // Pass through for system prompt caching
      ignoreBreakpoints: providerIgnoreBreakpoints,
      includeOptionalInputFieldsInSystemPrompt:
        options.includeOptionalInputFieldsInSystemPrompt ??
        this.options?.includeOptionalInputFieldsInSystemPrompt,
      structuredOutputFunctionName: this.structuredOutputFunctionFallback
        ? STRUCTURED_OUTPUT_FUNCTION_NAME
        : undefined,
      customTemplate: options.customTemplate ?? this.options?.customTemplate,
    };

    const instruction = this.getInstruction();
    this.promptTemplate = new promptTemplateClass(
      this.signature,
      currentPromptTemplateOptions
    );
    if (instruction !== undefined && instruction.trim().length > 0) {
      this.promptTemplate.setInstruction(instruction);
    }

    // Track prompt rendering performance
    const promptRenderStart = performance.now();

    const renderedInitialPrompt:
      | AxRenderedPrompt
      | { chatPrompt: AxChatRequest['chatPrompt'] } =
      'renderWithMetrics' in this.promptTemplate &&
      typeof this.promptTemplate.renderWithMetrics === 'function'
        ? this.promptTemplate.renderWithMetrics(values as any, {
            examples: this.examples as any,
            demos: this.demos as any,
          })
        : {
            chatPrompt: this.promptTemplate.render(values as any, {
              examples: this.examples as any,
              demos: this.demos as any,
            }),
          };

    const basePrompt = renderedInitialPrompt.chatPrompt;
    const firstNonSystem = basePrompt.findIndex(
      (message) => message.role !== 'system'
    );
    const contextInsertionIndex =
      firstNonSystem === -1 ? basePrompt.length : firstNonSystem;
    const prompt = options._mcpContextPrompt?.length
      ? [
          ...basePrompt.slice(0, contextInsertionIndex),
          ...options._mcpContextPrompt,
          ...basePrompt.slice(contextInsertionIndex),
        ]
      : basePrompt;
    const promptMetrics =
      'promptMetrics' in renderedInitialPrompt
        ? renderedInitialPrompt.promptMetrics
        : undefined;

    // Resolve routing once before any model request or tool execution. Rebuild
    // the prompt against the selected provider's actual capabilities.
    if (
      ai.resolveChatService &&
      (options.asyncMode ?? ai.getOptions().asyncMode) !== 'off' &&
      functionCallMode !== 'prompt' &&
      (options.control ||
        mutableFunctions.some((fn) => fn.execution === 'background'))
    ) {
      const selected = await ai.resolveChatService(
        {
          model: options.model,
          chatPrompt: prompt,
          functions: mutableFunctions,
          ...(this.structuredOutputRung === 'native'
            ? {
                responseFormat: {
                  type: 'json_schema' as const,
                  fieldDescriptions: outputValueDescriptions(
                    this.signature.getOutputFields()
                  ),
                  schema: {
                    name: 'output',
                    strict: true,
                    schema: toJsonSchema(
                      this.signature.getOutputFields(),
                      'Schema',
                      {
                        flexibleJsonFieldsAsString: true,
                        strictStructuredOutputs: true,
                      }
                    ),
                  },
                },
              }
            : {}),
        },
        options
      );
      if (selected.service === ai)
        throw new Error('AI routing must resolve to a different service');
      yield* this._forward2(
        selected.service,
        values,
        states,
        {
          ...options,
          model: selected.model,
        },
        span,
        traceContext
      );
      return;
    }

    const promptRenderDuration = performance.now() - promptRenderStart;

    // Record prompt render performance metric
    const metricsInstruments = this.getMetricsInstruments(
      this.getEffectiveMeter(ai, options)
    );
    const customLabels = this.getMergedCustomLabels(ai, options);
    if (metricsInstruments) {
      recordPerformanceMetric(
        metricsInstruments,
        'prompt_render',
        promptRenderDuration,
        this.getSignatureName(),
        customLabels
      );
    }

    // Track memory update performance
    const memoryUpdateStart = performance.now();
    mem.addRequest(prompt, options.sessionId);
    const memoryUpdateDuration = performance.now() - memoryUpdateStart;

    // Record memory update performance metric
    if (metricsInstruments) {
      recordPerformanceMetric(
        metricsInstruments,
        'memory_update',
        memoryUpdateDuration,
        this.getSignatureName(),
        customLabels
      );
    }

    // Track committed values across retries to prevent duplication
    const committedValues = new Map<number, Record<string, any>>();
    states.forEach((s) => {
      committedValues.set(s.index, {});
    });

    // Helper to apply pending step context mutations to mutableOptions/mutableFunctions
    const applyStepContextMutations = () => {
      const pendingOpts = stepContext._consumePendingOptions();
      if (pendingOpts) {
        const { modelConfig: pendingModelConfig, ...restOpts } = pendingOpts;
        mutableOptions = { ...mutableOptions, ...restOpts };
        if (pendingModelConfig) {
          mutableOptions.modelConfig = {
            ...mutableOptions.modelConfig,
            ...pendingModelConfig,
          };
        }
      }

      const toAdd = stepContext._consumeFunctionsToAdd();
      if (toAdd) {
        const parsed = parseFunctions(toAdd);
        assertNoReservedStructuredOutputFunctions(parsed);
        for (const fn of parsed) {
          if (!mutableFunctions.some((f) => f.name === fn.name)) {
            mutableFunctions.push(fn);
          }
        }
      }

      const toRemove = stepContext._consumeFunctionsToRemove();
      if (toRemove) {
        const removeSet = new Set(toRemove.map((n) => n.toLowerCase()));
        for (let i = mutableFunctions.length - 1; i >= 0; i--) {
          if (removeSet.has(mutableFunctions[i]!.name.toLowerCase())) {
            mutableFunctions.splice(i, 1);
          }
        }
      }
    };

    // Resolve the effective abort signal once at method start
    const effectiveAbortSignal = mergeAbortSignals(
      options?.abortSignal,
      axGlobals.abortSignal
    );

    let controlAfter = 0;
    let controlVersion = 0;
    const controlsUseSession =
      ai.getFeatures(options.model).asyncTools &&
      ai.openChatSession &&
      (options.asyncMode ?? ai.getOptions().asyncMode) !== 'off' &&
      options.functionCallMode !== 'prompt';

    // Versions never decrease. The version a consumer is merging is the last
    // one yielded; an attempt that starts errCount over (a new step or an
    // infrastructure retry) resumes from it instead of falling back to
    // controlVersion. Per index, the thought yielded in that version is kept
    // so a new version can carry it forward.
    let currentVersion = 0;
    let outputEmittedThisVersion = false;
    const emittedThought = new Map<number, string>();
    const trackYield = (delta: Readonly<AxGenDeltaOut<OUT>>) => {
      if (delta.version !== currentVersion) {
        currentVersion = delta.version;
        emittedThought.clear();
        outputEmittedThisVersion = false;
      }
      for (const [key, value] of Object.entries(delta.delta)) {
        if (key === this.thoughtFieldName && typeof value === 'string') {
          emittedThought.set(
            delta.index,
            (emittedThought.get(delta.index) ?? '') + value
          );
        } else {
          outputEmittedThisVersion = true;
        }
      }
    };

    multiStepLoop: for (let n = 0; n < maxSteps; n++) {
      // A step that follows emitted output fields replaces them: it starts a
      // new version (consumers discard older versions) and carries the
      // thought yielded so far into it, so thought still joins across steps.
      if (n > 0 && outputEmittedThisVersion) {
        controlVersion = Math.max(controlVersion, currentVersion) + 1;
        committedValues.forEach((_, index) => committedValues.set(index, {}));
        const carried = [...emittedThought.entries()];
        currentVersion = controlVersion;
        emittedThought.clear();
        outputEmittedThisVersion = false;
        for (const [index, thought] of carried) {
          if (!thought) continue;
          const delta = {
            version: controlVersion,
            index,
            delta: { [this.thoughtFieldName]: thought } as Partial<OUT>,
          };
          trackYield(delta);
          yield delta;
        }
      }

      if (!controlsUseSession && options.control) {
        const path = options.executionPath ?? 'root';
        for (const update of options.control.pending(path, controlAfter)) {
          controlAfter = update.id;
          if (update.type === 'steer')
            mem.addRequest(
              [{ role: 'user', content: update.text }],
              options.sessionId
            );
          else mutableOptions.thinkingTokenBudget = update.level;
          controlVersion = Math.max(controlVersion, currentVersion) + 1;
          committedValues.forEach((_, index) => committedValues.set(index, {}));
          options.control.emit({
            type: 'applied',
            path,
            updateId: update.id,
            timing: 'next-response',
          });
        }
      }
      // Begin new step on the context
      stepContext._beginStep(n);

      // Apply pending mutations from previous step (or from selfTuning/hooks)
      applyStepContextMutations();

      const nextMcpCatalogRevision =
        options._mcpExecutionContext?.getCatalogRevision();
      if (
        nextMcpCatalogRevision !== undefined &&
        nextMcpCatalogRevision !== mcpCatalogRevision
      ) {
        for (let i = mutableFunctions.length - 1; i >= 0; i--) {
          if (mutableFunctions[i]?.componentId?.startsWith('mcp:')) {
            mutableFunctions.splice(i, 1);
          }
        }
        mutableFunctions.push(
          ...options._mcpExecutionContext!.getToolBindings()
        );
        mcpCatalogRevision = nextMcpCatalogRevision;
      }

      // Update self-tuning function schema if model changed
      if (selfTuningConfig && selfTuningConfig.model !== false) {
        const idx = mutableFunctions.findIndex(
          (f) => f.name === 'adjustGeneration'
        );
        if (idx !== -1) {
          const currentModel = mutableOptions.model
            ? String(mutableOptions.model)
            : undefined;
          mutableFunctions[idx] = createSelfTuningFunction(
            ai,
            selfTuningConfig,
            currentModel
          );
        }
      }

      // Check if stop was requested by a previous step's function/hook
      if (stepContext._isStopRequested) {
        break;
      }

      // Check if abort was signalled between steps
      if (effectiveAbortSignal?.aborted) {
        throw new AxAIServiceAbortedError(
          'between-steps',
          effectiveAbortSignal.reason ?? 'Aborted between steps'
        );
      }

      // Call beforeStep hook
      if (stepHooks?.beforeStep) {
        await stepHooks.beforeStep(stepContext);
        applyStepContextMutations();
        if (stepContext._isStopRequested) {
          break;
        }
      }

      // Infrastructure error retry configuration
      // Use the same maxRetries for infrastructure errors (default 3)
      const infraMaxRetries = maxRetries;

      // Infrastructure retry loop (outer loop for 5xx, network, timeout, stream termination errors)
      for (
        let infraRetryCount = 0;
        infraRetryCount <= infraMaxRetries;
        infraRetryCount++
      ) {
        // errCount starts over below; resume from the version consumers hold.
        controlVersion = Math.max(controlVersion, currentVersion);
        try {
          // Validation error retry loop (inner loop).
          // `maxRetries` means extra attempts after the initial one.
          const validationAttemptCount = maxRetries + 1;
          for (
            let errCount = 0;
            errCount < validationAttemptCount;
            errCount++
          ) {
            // Reset states for new attempt
            states.forEach((s) => {
              s.content = '';
              s.values = {};
              s.functionCalls = [];
              s.functionsExecuted = new Set<string>();
              s.xstate = { extractedFields: [], streamedIndex: {}, s: -1 };
              s.structuredAccumulator = undefined;
            });

            // Reset committed values on retry so all values are re-emitted in new version
            if (errCount > 0) {
              committedValues.forEach((_, index) => {
                committedValues.set(index, {});
              });
            }

            // Track values for the current attempt to calculate deltas relative to committed values
            const currentAttemptValues = new Map<number, Record<string, any>>();
            states.forEach((s) => {
              currentAttemptValues.set(s.index, {});
            });

            try {
              const generator = this.forwardCore({
                options: { ...mutableOptions, functions: mutableFunctions },
                ai,
                values,
                mem,
                stepIndex: n,
                span,
                traceContext,
                states,
                stopFunctionNames,
                stepContext,
                preRenderedPrompt:
                  n === 0 &&
                  errCount === 0 &&
                  !controlAfter &&
                  !stepHooks?.beforeStep
                    ? { prompt, promptMetrics }
                    : undefined,
              });

              let stopFunctionTriggered = false;
              let lastSessionVersion = 0;
              try {
                for await (const result of generator) {
                  if (result !== undefined) {
                    const innerVersion = (
                      result as DeltaOut<OUT> & {
                        [axSessionOutputVersion]?: number;
                      }
                    )[axSessionOutputVersion];
                    if (
                      innerVersion !== undefined &&
                      innerVersion !== lastSessionVersion
                    ) {
                      lastSessionVersion = innerVersion;
                      controlVersion =
                        Math.max(controlVersion, currentVersion - errCount) + 1;
                      committedValues.forEach((_, index) =>
                        committedValues.set(index, {})
                      );
                      currentAttemptValues.forEach((_, index) =>
                        currentAttemptValues.set(index, {})
                      );
                    }
                    const index = result.index;
                    const delta = result.delta;

                    // Update current attempt values and calculate effective delta against committed values
                    const currentValues = currentAttemptValues.get(index) ?? {};
                    const committed = committedValues.get(index) ?? {};
                    const effectiveDelta: Partial<OUT> = {};
                    let hasEffectiveDelta = false;

                    for (const key of Object.keys(delta)) {
                      const dVal = (delta as any)[key];
                      const curVal = currentValues[key];

                      // Merge into currentValues
                      let newVal: any;
                      if (
                        typeof dVal === 'string' &&
                        (typeof curVal === 'string' || curVal === undefined)
                      ) {
                        newVal = (curVal ?? '') + dVal;
                      } else if (
                        Array.isArray(dVal) &&
                        (Array.isArray(curVal) || curVal === undefined)
                      ) {
                        newVal = [...(curVal ?? []), ...dVal];
                      } else {
                        newVal = dVal;
                      }
                      currentValues[key] = newVal;

                      // Now compare with committed
                      const val = newVal;
                      const committedVal = committed[key];

                      if (
                        typeof val === 'string' &&
                        typeof committedVal === 'string'
                      ) {
                        if (val.startsWith(committedVal)) {
                          const diff = val.slice(committedVal.length);
                          if (diff) {
                            (effectiveDelta as any)[key] = diff;
                            hasEffectiveDelta = true;
                            committed[key] = val; // Update committed value
                          }
                        } else if (committedVal.startsWith(val)) {
                          // Replay of previously yielded value - suppress
                        } else {
                          // Divergence or new value, assume overwrite/append
                          if (val !== committedVal) {
                            (effectiveDelta as any)[key] = val;
                            hasEffectiveDelta = true;
                            committed[key] = val;
                          }
                        }
                      } else if (
                        Array.isArray(val) &&
                        Array.isArray(committedVal)
                      ) {
                        // For arrays, if val is superset of committed
                        if (val.length > committedVal.length) {
                          // Check if it's a pure extension
                          // Simple check: compare JSON of prefix
                          // Or just slice and trust?
                          // For streaming arrays, we usually just append items.
                          const diff = val.slice(committedVal.length);
                          (effectiveDelta as any)[key] = diff;
                          hasEffectiveDelta = true;
                          committed[key] = val;
                        }
                        // If val is subset of committed (replay), do nothing.
                      } else {
                        // Other types (boolean, number, object), yield if changed
                        if (!areValuesEqual(val, committedVal)) {
                          (effectiveDelta as any)[key] = val;
                          hasEffectiveDelta = true;
                          committed[key] = val;
                        }
                      }
                    }

                    if (hasEffectiveDelta) {
                      const out = {
                        version: controlVersion + errCount,
                        index: result.index,
                        delta: effectiveDelta,
                      };
                      trackYield(out);
                      yield out;
                    }
                  }
                }
              } catch (e) {
                if (e instanceof AxStopFunctionCallException) {
                  stopFunctionTriggered = true;
                  let stopOutputYielded = false;

                  // Extract structured output values from the synthetic function call
                  if (this.structuredOutputFunctionFallback) {
                    const structuredCall = e.calls.find((c) =>
                      isReservedStructuredOutputFunctionName(c.func.name)
                    );
                    if (structuredCall?.args) {
                      stopOutputYielded = true;
                      const args = structuredCall.args as Record<
                        string,
                        unknown
                      >;

                      // Validate against field constraints (same as native structured output path)
                      validateStructuredOutputValues(this.signature, args, {
                        rejectUnknownFields: true,
                      });

                      const outputFields = this.signature.getOutputFields();
                      for (const state of states) {
                        for (const field of outputFields) {
                          if (field.name in args) {
                            state.values[field.name] = args[field.name];
                          }
                        }
                      }

                      // Run field processors (same as native structured output path)
                      if (this.fieldProcessors.length > 0) {
                        for (const state of states) {
                          await processFieldProcessors(
                            this.fieldProcessors,
                            state.values as OUT,
                            mem,
                            options.sessionId
                          );
                        }
                      }

                      // Run assertions (same as native structured output path)
                      for (const state of states) {
                        await assertAssertions(
                          this.asserts,
                          state.values as OUT
                        );
                      }

                      for (const state of states) {
                        const delta: Record<string, unknown> = {};
                        for (const field of outputFields) {
                          if (field.name in state.values && !field.isInternal) {
                            delta[field.name] = state.values[field.name];
                          }
                        }
                        // The response's thought is already on state.values.
                        // A stream has yielded it chunk by chunk; a
                        // non-streaming response has not, so keep it here as
                        // the native path does.
                        const thought = state.values[this.thoughtFieldName];
                        const thoughtYielded =
                          currentAttemptValues.get(state.index)?.[
                            this.thoughtFieldName
                          ] !== undefined;
                        if (thought !== undefined && !thoughtYielded) {
                          delta[this.thoughtFieldName] = thought;
                        }
                        const out = {
                          version: controlVersion + errCount,
                          index: state.index,
                          delta: delta as Partial<OUT>,
                        };
                        trackYield(out);
                        yield out;
                      }
                    }
                  }

                  // A user stop function ends the run on this step, and the
                  // step's thought stays in the output: a stream has yielded
                  // it chunk by chunk, a non-streaming response has not.
                  if (!stopOutputYielded) {
                    for (const state of states) {
                      const thought = state.values[this.thoughtFieldName];
                      const thoughtYielded =
                        currentAttemptValues.get(state.index)?.[
                          this.thoughtFieldName
                        ] !== undefined;
                      if (thought === undefined || thoughtYielded) {
                        continue;
                      }
                      const out = {
                        version: controlVersion + errCount,
                        index: state.index,
                        delta: {
                          [this.thoughtFieldName]: thought,
                        } as Partial<OUT>,
                      };
                      trackYield(out);
                      yield out;
                    }
                  }
                } else {
                  throw e;
                }
              }

              // Accumulate usage on step context from this step's usage data
              if (this.usage.length > 0) {
                const lastUsage = this.usage[this.usage.length - 1];
                if (lastUsage?.tokens) {
                  stepContext._addUsage(
                    lastUsage.tokens.promptTokens ?? 0,
                    lastUsage.tokens.completionTokens ?? 0,
                    lastUsage.tokens.totalTokens ?? 0
                  );
                }
              }

              // Check if any functions were executed (for afterFunctionExecution hook)
              const functionsRan = states.some(
                (s) => s.functionsExecuted.size > 0
              );

              // Call afterFunctionExecution hook if functions ran
              if (functionsRan && stepHooks?.afterFunctionExecution) {
                await stepHooks.afterFunctionExecution(stepContext);
                applyStepContextMutations();
              }

              const shouldContinue =
                stopFunctionTriggered || stepContext._isStopRequested
                  ? false
                  : (!controlsUseSession &&
                      !!options.control?.pending(
                        options.executionPath ?? 'root',
                        controlAfter
                      ).length) ||
                    shouldContinueSteps(
                      mem,
                      stopFunctionNames,
                      states,
                      mutableOptions?.sessionId
                    );

              // Call afterStep hook
              if (stepHooks?.afterStep) {
                await stepHooks.afterStep(stepContext);
                applyStepContextMutations();
              }

              if (
                shouldContinue &&
                !stepContext._isStopRequested &&
                !effectiveAbortSignal?.aborted
              ) {
                // Record multi-step generation metric
                const metricsInstruments = this.getMetricsInstruments(
                  this.getEffectiveMeter(ai, options)
                );
                if (metricsInstruments) {
                  recordMultiStepMetric(
                    metricsInstruments,
                    n + 1,
                    maxSteps,
                    this.getSignatureName(),
                    customLabels
                  );
                }
                continue multiStepLoop;
              }

              // If we stopped because of an abort signal, throw
              if (effectiveAbortSignal?.aborted) {
                throw new AxAIServiceAbortedError(
                  'mid-step',
                  effectiveAbortSignal.reason ?? 'Aborted'
                );
              }

              // On success, clean up any error-related tags from memory to keep context clean
              if (!options?.disableMemoryCleanup) {
                mem.removeByTag('invalid-assistant', options.sessionId);
                mem.removeByTag('correction', options.sessionId);
                mem.removeByTag('error', options.sessionId);
              }

              // Record successful completion metrics
              const metricsInstruments = this.getMetricsInstruments(
                this.getEffectiveMeter(ai, options)
              );
              if (metricsInstruments) {
                recordMultiStepMetric(
                  metricsInstruments,
                  n + 1,
                  maxSteps,
                  this.getSignatureName(),
                  customLabels
                );

                // Count unique functions executed across all states
                const allFunctionsExecuted = new Set<string>();
                states.forEach((state) => {
                  state.functionsExecuted.forEach((func) =>
                    allFunctionsExecuted.add(func)
                  );
                });

                // Record function metrics if functions were used
                if (allFunctionsExecuted.size > 0) {
                  recordFunctionCallingMetric(
                    metricsInstruments,
                    true,
                    allFunctionsExecuted.size,
                    true,
                    false,
                    this.getSignatureName(),
                    customLabels
                  );
                }

                // Record field processing metrics
                recordFieldProcessingMetric(
                  metricsInstruments,
                  this.fieldProcessors.length,
                  this.streamingFieldProcessors.length,
                  this.getSignatureName(),
                  customLabels
                );
              }

              return;
            } catch (e) {
              // Re-throw abort errors immediately — never retry or wrap them
              if (e instanceof AxAIServiceAbortedError) {
                throw e;
              }

              lastError = e as Error;
              lastOutput = states.map((s) => s.content).join('\n---\n');
              let errorFields: AxIField[] | undefined;
              const debug = this.isDebug(ai, options);
              const logger = this.getLogger(ai, options);
              const metricsInstruments = this.getMetricsInstruments(
                this.getEffectiveMeter(ai, options)
              );
              const signatureName = this.getSignatureName();

              const args: HandleErrorForGenerateArgs<Error> = {
                error: e as Error,
                errCount,
                logger,
                metricsInstruments,
                signatureName,
                span,
                debug,
                customLabels,
              };

              span?.recordException(e as Error);

              if (e instanceof ValidationError) {
                errorFields = handleValidationErrorForGenerate(
                  args as HandleErrorForGenerateArgs<ValidationError>
                );
                err = e;
              } else if (e instanceof AxAssertionError) {
                errorFields = handleValidationErrorForGenerate(
                  args as HandleErrorForGenerateArgs<AxAssertionError>
                );
                err = e;
              } else if (e instanceof AxStreamingAssertionError) {
                errorFields = handleValidationErrorForGenerate(
                  args as HandleErrorForGenerateArgs<AxStreamingAssertionError>
                );
                err = e;
              } else if (e instanceof AxAIRefusalError) {
                handleRefusalErrorForGenerate(
                  args as HandleErrorForGenerateArgs<AxAIRefusalError>
                );
              } else if (e instanceof AxAIServiceStreamTerminatedError) {
                // Route stream termination to infrastructure retry logic
                throw e;
              } else {
                // Check if this is a retryable infrastructure error
                // If so, let it bubble up to the infrastructure retry loop
                const error = e as Error;
                const isInfraError =
                  error instanceof AxAIServiceStatusError &&
                  (error as AxAIServiceStatusError).status >= 500 &&
                  (error as AxAIServiceStatusError).status < 600;

                const isNetworkError = error instanceof AxAIServiceNetworkError;
                const isTimeoutError = error instanceof AxAIServiceTimeoutError;

                if (isInfraError || isNetworkError || isTimeoutError) {
                  // Let infrastructure errors bubble up to outer catch
                  throw e;
                }

                // Not an infrastructure error, enhance and throw
                throw enhanceError(e, ai, this.signature);
              }

              if (errorFields) {
                mem.addTag('error', options.sessionId);
                mem.addRequest(
                  [
                    {
                      role: 'user' as const,
                      content:
                        this.promptTemplate.renderExtraFields(errorFields),
                    },
                  ],
                  options.sessionId
                );
                mem.addTag('correction', options.sessionId);

                // When using structured outputs (JSON mode), we need to reset the state content
                // to avoid concatenating JSON objects from previous retry attempts
                const hasComplexFields =
                  this.signature.hasComplexFields() ||
                  this.structuredOutputRung !== undefined;
                if (hasComplexFields) {
                  for (const state of states) {
                    state.content = '';
                    state.values = {};
                    state.xstate = {
                      extractedFields: [],
                      streamedIndex: {},
                      s: -1,
                    };
                    state.structuredAccumulator = undefined;
                  }
                }
              }
            }
          }

          // Record max retries reached for validation errors
          const metricsInstruments = this.getMetricsInstruments(
            this.getEffectiveMeter(ai, options)
          );
          if (metricsInstruments) {
            recordErrorCorrectionMetric(
              metricsInstruments,
              maxRetries,
              false, // failed
              maxRetries,
              this.getSignatureName(),
              customLabels
            );
          }

          if (err instanceof AxStreamingAssertionError) {
            throw enhanceError(err, ai, this.signature);
          }

          throw enhanceError(
            new Error(
              `Unable to fix validation error: ${
                (err ?? lastError)?.message ??
                (err ?? lastError)?.toString() ??
                'unknown error'
              }\n\nLLM Output:\n${lastOutput}`
            ),
            ai,
            this.signature
          );
        } catch (e) {
          // Infrastructure error handling
          const error = e as Error;
          const isInfraError =
            error instanceof AxAIServiceStatusError &&
            (error as AxAIServiceStatusError).status >= 500 &&
            (error as AxAIServiceStatusError).status < 600;

          const isNetworkError = error instanceof AxAIServiceNetworkError;
          const isTimeoutError = error instanceof AxAIServiceTimeoutError;
          const isStreamTerminated =
            error instanceof AxAIServiceStreamTerminatedError;

          const shouldRetryInfra =
            (isInfraError ||
              isNetworkError ||
              isTimeoutError ||
              isStreamTerminated) &&
            infraRetryCount < infraMaxRetries;

          if (shouldRetryInfra) {
            const debug = this.isDebug(ai, options);
            const logger = this.getLogger(ai, options);

            // Calculate exponential backoff delay
            const baseDelay = 1000; // 1 second
            const maxDelay = 60000; // 60 seconds
            const delay = Math.min(
              maxDelay,
              baseDelay * Math.pow(2, infraRetryCount)
            );

            if (debug && logger) {
              logger({
                name: 'Notification',
                id: 'infrastructure-retry',
                value: `Infrastructure error (attempt ${infraRetryCount + 1}/${infraMaxRetries + 1}): ${error.message}. Retrying in ${delay}ms...`,
              });
            }

            span?.addEvent('infrastructure.retry', {
              attempt: infraRetryCount + 1,
              maxRetries: infraMaxRetries,
              delay,
              errorType:
                error instanceof AxAIServiceStatusError
                  ? 'status_error'
                  : error instanceof AxAIServiceNetworkError
                    ? 'network_error'
                    : error instanceof AxAIServiceTimeoutError
                      ? 'timeout_error'
                      : 'stream_terminated',
              errorMessage: error.message,
            });

            // Wait before retrying
            await new Promise<void>((resolve, reject) => {
              let settled = false;
              let onAbort: (() => void) | undefined;
              const cleanup = () => {
                if (effectiveAbortSignal && onAbort) {
                  effectiveAbortSignal.removeEventListener('abort', onAbort);
                }
              };
              const onResolve = () => {
                if (settled) return;
                settled = true;
                cleanup();
                resolve();
              };
              const timer = setTimeout(onResolve, delay);

              // Allow stop/abort to interrupt long backoff sleeps
              if (!effectiveAbortSignal) {
                return;
              }

              onAbort = () => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                cleanup();
                reject(
                  new AxAIServiceAbortedError(
                    'infrastructure-retry-backoff',
                    effectiveAbortSignal.reason
                      ? String(effectiveAbortSignal.reason)
                      : 'Aborted during retry backoff'
                  )
                );
              };

              if (effectiveAbortSignal.aborted) {
                onAbort();
                return;
              }

              effectiveAbortSignal.addEventListener('abort', onAbort, {
                once: true,
              });
            });
            continue; // Retry infrastructure call
          }

          // Not a retryable infrastructure error, or max retries exhausted
          throw e;
        }
      }
    }

    // Record max steps reached
    if (metricsInstruments) {
      recordMultiStepMetric(
        metricsInstruments,
        maxSteps,
        maxSteps,
        this.getSignatureName(),
        customLabels
      );
    }

    throw enhanceError(
      new Error(`Max steps reached: ${maxSteps}`),
      ai,
      this.signature
    );
  }

  /**
   * Validate input values against field constraints
   * @throws ValidationError if any input value fails validation
   */
  private validateInputs(values: IN): void {
    const inputFields = this.signature.getInputFields();

    for (const field of inputFields) {
      if (field.isInternal) continue;

      const value = values[field.name as keyof IN];

      // Skip validation for optional fields with undefined values
      if (field.isOptional && value === undefined) {
        continue;
      }

      const type = field.type;
      if (!type) continue;

      // Validate based on field type
      if (type.name === 'url') {
        validateURL(value, field);
      }

      if (type.name === 'date') {
        // Date validation is already handled by existing parseLLMFriendlyDate
        // which is called during extraction - we'll rely on that
      }

      if (type.name === 'datetime') {
        // DateTime validation is already handled by existing parseLLMFriendlyDateTime
        // which is called during extraction - we'll rely on that
      }

      if (type.name === 'string' || type.name === 'code') {
        validateStringConstraints(value, field);
      }

      if (type.name === 'number') {
        validateNumberConstraints(value, field);
      }

      // Recursively validate object fields
      if (
        type.name === 'object' &&
        type.fields &&
        typeof value === 'object' &&
        value !== null
      ) {
        this.validateObjectFields(
          value as Record<string, unknown>,
          type.fields,
          field.name
        );
      }

      // Validate array elements
      if (type.isArray && Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
          const item = value[i];

          if (type.name === 'string' || type.name === 'code') {
            validateStringConstraints(item, field);
          } else if (type.name === 'number') {
            validateNumberConstraints(item, field);
          } else if (type.fields && typeof item === 'object' && item !== null) {
            this.validateObjectFields(
              item as Record<string, unknown>,
              type.fields,
              `${field.name}[${i}]`
            );
          }
        }
      }
    }
  }

  /**
   * Recursively validate object field values
   */
  private validateObjectFields(
    obj: Record<string, unknown>,
    fields: Record<string, import('./sig.js').AxFieldType>,
    parentFieldName: string
  ): void {
    for (const [fieldName, fieldType] of Object.entries(fields)) {
      const value = obj[fieldName];

      // Skip optional fields with undefined values
      if (fieldType.isOptional && value === undefined) {
        continue;
      }

      const syntheticField: import('./sig.js').AxField = {
        name: `${parentFieldName}.${fieldName}`,
        type: {
          name: fieldType.type,
          isArray: fieldType.isArray,
          options: fieldType.options ? [...fieldType.options] : undefined,
          fields: fieldType.fields,
          minLength: fieldType.minLength,
          maxLength: fieldType.maxLength,
          minimum: fieldType.minimum,
          maximum: fieldType.maximum,
          pattern: fieldType.pattern,
          format: fieldType.format,
        },
        description: fieldType.description,
        isOptional: fieldType.isOptional,
      };

      if (fieldType.type === 'string' || fieldType.type === 'code') {
        validateStringConstraints(value, syntheticField);
      } else if (fieldType.type === 'number') {
        validateNumberConstraints(value, syntheticField);
      } else if (
        fieldType.type === 'object' &&
        fieldType.fields &&
        typeof value === 'object' &&
        value !== null
      ) {
        this.validateObjectFields(
          value as Record<string, unknown>,
          fieldType.fields,
          syntheticField.name
        );
      }

      // Validate arrays
      if (fieldType.isArray && Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
          const item = value[i];

          if (fieldType.type === 'string' || fieldType.type === 'code') {
            validateStringConstraints(item, syntheticField);
          } else if (fieldType.type === 'number') {
            validateNumberConstraints(item, syntheticField);
          } else if (
            fieldType.fields &&
            typeof item === 'object' &&
            item !== null
          ) {
            this.validateObjectFields(
              item as Record<string, unknown>,
              fieldType.fields,
              `${syntheticField.name}[${i}]`
            );
          }
        }
      }
    }
  }

  public async *_forward1(
    ai: Readonly<AxAIService>,
    values: IN,
    callOptions: Readonly<AxProgramForwardOptions<any>>
  ): AxGenStreamingOut<OUT> {
    const options = this.withConstructorDefaults(callOptions);
    this.validateInputs(values);

    const inheritedHookFrame = axGetRuntimeHookFrame(options);
    const globalHooks =
      inheritedHookFrame?.globals ??
      Object.freeze({
        rateLimiter: axGlobals.rateLimiter,
        tracer: axGlobals.tracer,
        meter: axGlobals.meter,
      });

    // Create internal abort controller and merge with any user-provided signal
    const abortController = new AbortController();
    this.activeAbortControllers.add(abortController);
    if (this._stopRequested) {
      abortController.abort('Stopped by user (pre-forward)');
    }
    const effectiveAbortSignal = mergeAbortSignals(
      abortController.signal,
      mergeAbortSignals(
        mergeAbortSignals(options?.abortSignal, options?.control?.signal),
        axGlobals.abortSignal
      )
    );
    const effectiveOptions = {
      ...options,
      rateLimiter:
        options.rateLimiter ??
        this.options?.rateLimiter ??
        ai.getOptions().rateLimiter ??
        globalHooks.rateLimiter,
      tracer:
        options.tracer ??
        this.options?.tracer ??
        ai.getOptions().tracer ??
        globalHooks.tracer,
      meter: this.getEffectiveMeter(ai, options),
      [axRuntimeHookFrame]: { globals: globalHooks, resolved: true },
      ...(effectiveAbortSignal ? { abortSignal: effectiveAbortSignal } : {}),
    };

    let runSucceeded = false;
    let runFailed = false;
    let runFailure: unknown;
    options.control?.emit({
      type: 'started',
      path: options.executionPath ?? 'root',
    });
    try {
      // Track state creation performance
      const stateCreationStart = performance.now();
      const states = this.createStates(options.sampleCount ?? 1);
      const stateCreationDuration = performance.now() - stateCreationStart;

      // Record state creation performance metric
      const metricsInstruments = this.getMetricsInstruments(
        effectiveOptions.meter
      );
      const customLabels = this.getMergedCustomLabels(ai, options);
      if (metricsInstruments) {
        recordPerformanceMetric(
          metricsInstruments,
          'state_creation',
          stateCreationDuration,
          this.getSignatureName(),
          customLabels
        );
      }

      const tracer = effectiveOptions.tracer;

      let functions: AxFunction[] | undefined = this.functions;

      if (options?.functions) {
        functions = parseFunctions(options.functions, this.functions);
      }

      const mcpExecutionContext = await axResolveMCPExecutionContext(
        options,
        this.options
      );
      if (mcpExecutionContext) {
        functions = [
          ...(functions ?? []),
          ...mcpExecutionContext.getToolBindings(),
        ];
      }
      const mcpContextPrompt = mcpExecutionContext
        ? await mcpExecutionContext.resolveContextPrompt(
            options.mcpContext ?? this.options?.mcpContext
          )
        : undefined;
      const executionOptions = mcpExecutionContext
        ? {
            ...effectiveOptions,
            _mcpExecutionContext: mcpExecutionContext,
            ...(mcpContextPrompt?.length
              ? { _mcpContextPrompt: mcpContextPrompt }
              : {}),
          }
        : effectiveOptions;

      const funcNames = functions?.map((f) => f.name).join(',');

      const attributes = {
        signature_name: this.getSignatureName(),
        input_field_count: this.signature.getInputFields().length,
        output_field_count: this.signature.getOutputFields().length,
        ...(funcNames ? { provided_functions: funcNames } : {}),
        ...(options?.model ? { model: options.model } : {}),
        ...(options?.thinkingTokenBudget
          ? { thinking_token_budget: options.thinkingTokenBudget }
          : {}),
        ...(options?.showThoughts
          ? { show_thoughts: options.showThoughts }
          : {}),
        ...(options?.maxSteps ? { max_steps: options.maxSteps } : {}),
        ...(options?.maxRetries ? { max_retries: options.maxRetries } : {}),
      };

      const traceLabel =
        this.traceLabel && options.traceLabel
          ? `${this.traceLabel} > ${options.traceLabel}`
          : (options.traceLabel ?? this.traceLabel);
      const spanName = traceLabel ? `AxGen > ${traceLabel}` : 'AxGen';

      const parentContext = options.traceContext ?? context.active();
      const span = axStartSpanFailOpen(
        tracer,
        spanName,
        {
          kind: SpanKind.SERVER,
          attributes,
        },
        parentContext
      );

      if (!span) {
        yield* this._forward2(ai, values, states, {
          ...executionOptions,
          functions,
        });
        runSucceeded = true;
        return;
      }

      const traceContext = trace.setSpan(parentContext, span);

      try {
        span.addEvent('input', {
          field_count: Object.keys(values as Record<string, unknown>).length,
        });

        yield* this._forward2(
          ai,
          values,
          states,
          {
            ...executionOptions,
            functions,
          },
          span,
          traceContext
        );

        runSucceeded = true;
        span.addEvent('output', {
          sample_count: states.length,
          field_count: Math.max(
            0,
            ...states.map((state) => Object.keys(state.values).length)
          ),
        });
      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        span.recordException(err);
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: err.message,
        });
        throw error;
      } finally {
        span.end();
      }
    } catch (error) {
      runFailed = true;
      runFailure = error;
      throw error;
    } finally {
      const path = options.executionPath ?? 'root';
      if (runSucceeded || runFailed) {
        options.control?.emit({
          type: runSucceeded ? 'completed' : 'failed',
          path,
          error: runFailure,
        });
      } else {
        // The consumer stopped the run early, for example with `break` in a
        // `for await` over streamingForward: it ended on purpose, as with
        // control.abort().
        options.control?.emit({ type: 'aborted', path });
      }
      abortController.abort(new Error('Run finished'));
      this.activeAbortControllers.delete(abortController);
      this._stopRequested = false;
    }
  }

  /**
   * Executes the generator with the given AI service and input values.
   *
   * This is the main entry point for running an AI generation. The execution pipeline:
   * 1. **Validate** - Check input values match the signature
   * 2. **Render** - Build the prompt from signature, examples, and inputs
   * 3. **Call** - Send the request to the AI service
   * 4. **Parse** - Extract structured outputs from the response
   * 5. **Validate** - Validate parsed outputs and retry with error correction if needed
   *
   * @param ai - The AI service instance to use (created via `ai()` factory)
   * @param values - Input values matching the signature's input fields
   * @param options - Optional execution configuration
   *
   * @param options.model - Override the default model for this request
   * @param options.maxTokens - Maximum tokens in the response. Rule of thumb: ~750 tokens ≈ 1 page
   *   of English text. Set higher for long-form content, lower for concise responses.
   * @param options.temperature - Controls randomness in generation (0-2):
   *   - `0` - Deterministic, always picks most likely token (best for factual tasks)
   *   - `0.3-0.7` - Balanced creativity (good for most tasks)
   *   - `1.0+` - High creativity (good for brainstorming, creative writing)
   *   - `2.0` - Maximum randomness (often incoherent)
   * @param options.thinkingTokenBudget - Enable extended thinking for complex reasoning:
   *   - `'none'` - Disabled (default)
   *   - `'minimal'` - ~1K tokens of thinking
   *   - `'low'` - ~4K tokens
   *   - `'medium'` - ~10K tokens
   *   - `'high'` - ~20K tokens
   *   - `'highest'` - ~32K+ tokens (provider maximum)
   * @param options.stream - Enable streaming responses for real-time output
   * @param options.functions - Array of function tools the AI can call
   * @param options.functionCallMode - How to handle function calling:
   *   - `'auto'` - Let the provider decide (default)
   *   - `'native'` - Force native function calling (if supported)
   *   - `'prompt'` - Simulate via prompt engineering (for models without native support)
   * @param options.mem - Memory instance for conversation history
   * @param options.sessionId - Session identifier for memory isolation
   * @param options.maxRetries - Maximum error correction attempts (default: 3)
   * @param options.maxSteps - Maximum function call iterations (default: 25)
   * @param options.debug - Enable debug logging
   *
   * @returns Promise resolving to the output values matching the signature's output fields
   *
   * @throws {AxValidationError} When input values don't match the signature
   * @throws {ValidationError} When output parsing/validation fails after all retries
   * @throws {AxAIServiceError} When the AI service request fails
   *
   * @example Basic usage
   * ```typescript
   * const gen = ax('question: string -> answer: string');
   * const result = await gen.forward(ai, { question: 'What is 2+2?' });
   * console.log(result.answer); // "4"
   * ```
   *
   * @example With configuration
   * ```typescript
   * const result = await gen.forward(ai, { question: 'Explain quantum computing' }, {
   *   maxTokens: 2000,
   *   temperature: 0.3,
   *   stream: true
   * });
   * ```
   *
   * @example Multi-turn conversation
   * ```typescript
   * const mem = new AxMemory();
   * const chat = ax('message: string -> reply: string');
   *
   * await chat.forward(ai, { message: 'Hi, my name is Alice' }, { mem });
   * const result = await chat.forward(ai, { message: 'What is my name?' }, { mem });
   * // result.reply will reference "Alice" from conversation history
   * ```
   *
   * @example With function calling
   * ```typescript
   * const result = await gen.forward(ai, values, {
   *   functions: [{
   *     name: 'getWeather',
   *     description: 'Get current weather for a city',
   *     parameters: {
   *       type: 'object',
   *       properties: { city: { type: 'string', description: 'City name' } },
   *       required: ['city']
   *     },
   *     func: async ({ city }) => fetchWeather(city)
   *   }],
   *   maxSteps: 5
   * });
   * ```
   */
  public async forward<T extends Readonly<AxAIService>>(
    ai: T,
    values: IN,
    callOptions?: Readonly<AxProgramForwardOptionsWithModels<T>>
  ): Promise<OUT> {
    const options = this.withConstructorDefaults(callOptions);
    // Caching pre-check: if cachingFunction provided and returns a value, short-circuit
    const cachingFunction =
      options?.cachingFunction ??
      this.options?.cachingFunction ??
      axGlobals.cachingFunction;
    const cacheKey = (() => {
      if (!cachingFunction || options?.control) return undefined;
      const inputNames = this.signature.getInputFields().map((f) => f.name);
      return this.computeCacheKey(values, inputNames);
    })();
    if (cachingFunction && cacheKey) {
      const cached = await cachingFunction(cacheKey);
      if (cached !== undefined) {
        return (await renderAudioOutputArtifacts(
          ai,
          this.signature,
          cached as AxGenOut,
          options
        )) as unknown as OUT;
      }
    }

    const startTime = performance.now();
    const signatureName = this.getSignatureName();
    const isStreaming = options?.stream ?? false;
    let success = false;
    let errorCorrectionAttempts = 0;
    let resultPickerUsed = false;

    try {
      // Record signature complexity metrics
      const metricsInstruments = this.getMetricsInstruments(
        this.getEffectiveMeter(ai, options)
      );
      const customLabels = this.getMergedCustomLabels(ai, options);
      if (metricsInstruments) {
        recordSignatureComplexityMetrics(
          metricsInstruments,
          this.signature.getInputFields().length,
          this.signature.getOutputFields().length,
          this.examples?.length ?? 0,
          this.demos?.length ?? 0,
          signatureName,
          customLabels
        );
      }

      const generator = this._forward1(ai, values, options ?? {});

      let buffer: AxGenDeltaOut<OUT>[] = [];
      let currentVersion = 0;
      let deltasEmitted = 0;

      for await (const delta of generator) {
        if (delta.version !== currentVersion) {
          buffer = [];
        }
        currentVersion = delta.version;
        buffer = mergeDeltas<OUT>(buffer, delta);
        deltasEmitted++;
      }

      // Track error correction attempts from the version count
      errorCorrectionAttempts = currentVersion;

      // Use result picker to select from multiple samples
      const resultPickerStart = performance.now();
      resultPickerUsed = !!options?.resultPicker;

      const selectedIndex = await selectFromSamples(
        buffer,
        {
          resultPicker: options?.resultPicker as
            | AxResultPickerFunction<OUT>
            | undefined,
        },
        // Pass memory to enable function result selection
        options?.mem,
        options?.sessionId
      );

      const resultPickerLatency = performance.now() - resultPickerStart;

      const selectedResult = buffer[selectedIndex];
      const result = await renderAudioOutputArtifacts(
        ai,
        this.signature,
        (selectedResult?.delta ?? {}) as AxGenOut,
        options
      );

      const baseTrace = (values as unknown as Record<string, unknown>) ?? {};
      this.trace = { ...baseTrace, ...result } as unknown as OUT;
      // Log result picker usage if it was used and debug is enabled
      if (resultPickerUsed && this.isDebug(ai, options)) {
        const logger = this.getLogger(ai, options);
        logResultPickerUsed(
          buffer.length,
          selectedIndex,
          resultPickerLatency,
          logger
        );
      }

      success = true;

      // Record samples metrics
      if (metricsInstruments) {
        recordSamplesMetric(
          metricsInstruments,
          buffer.length,
          resultPickerUsed,
          resultPickerUsed ? resultPickerLatency : undefined,
          signatureName,
          customLabels
        );

        // Record streaming metrics
        recordStreamingMetric(
          metricsInstruments,
          isStreaming,
          deltasEmitted,
          undefined, // finalization latency not applicable here
          signatureName,
          customLabels
        );
      }

      // Caching post-store: call cachingFunction again with value if provided
      if (cachingFunction && cacheKey) {
        try {
          await cachingFunction(cacheKey, result);
        } catch {}
      }

      return result as unknown as OUT;
    } catch (error) {
      success = false;
      throw error;
    } finally {
      const duration = performance.now() - startTime;

      // Record generation metrics
      const finalMetricsInstruments = this.getMetricsInstruments(
        this.getEffectiveMeter(ai, options)
      );
      const finalCustomLabels = this.getMergedCustomLabels(ai, options);
      if (finalMetricsInstruments) {
        recordGenerationMetric(
          finalMetricsInstruments,
          duration,
          success,
          signatureName,
          ai.getName(),
          options?.model ? String(options.model) : undefined,
          finalCustomLabels
        );

        // Skip per-call function execution metric here; detailed metrics are recorded during processing

        // Record error correction metrics
        if (errorCorrectionAttempts > 0) {
          recordErrorCorrectionMetric(
            finalMetricsInstruments,
            errorCorrectionAttempts,
            success,
            options?.maxRetries ?? this.options?.maxRetries ?? 3,
            signatureName,
            finalCustomLabels
          );
        }
      }
    }
  }

  async *streamingForward<T extends Readonly<AxAIService>>(
    ai: T,
    values: IN,
    callOptions?: Readonly<AxProgramStreamingForwardOptionsWithModels<T>>
  ): AxGenStreamingOut<OUT> {
    const options = this.withConstructorDefaults(callOptions);
    // Caching pre-check for streaming
    const cachingFunction =
      options?.cachingFunction ??
      this.options?.cachingFunction ??
      axGlobals.cachingFunction;
    const cacheKey = (() => {
      if (!cachingFunction || options?.control) return undefined;
      const inputNames = this.signature.getInputFields().map((f) => f.name);
      return this.computeCacheKey(values, inputNames);
    })();
    if (cachingFunction && cacheKey) {
      let cached: unknown;
      try {
        cached = await cachingFunction(cacheKey);
      } catch {}
      if (cached !== undefined) {
        const renderedCached = await renderAudioOutputArtifacts(
          ai,
          this.signature,
          cached as AxGenOut,
          options
        );
        yield {
          version: 0,
          index: 0,
          delta: renderedCached as unknown as OUT,
        };
        return;
      }
    }

    // If no result picker, use normal streaming
    if (!options?.resultPicker) {
      let streamed: AxGenDeltaOut<OUT>[] = [];
      let streamedVersion = 0;
      for await (const delta of this._forward1(ai, values, {
        ...options,
        stream: true,
      })) {
        if (delta.version !== streamedVersion) {
          streamed = [];
        }
        streamedVersion = delta.version;
        // mergeDeltas keeps and extends the first delta object, so merge a
        // copy and leave the yielded deltas as the consumer received them.
        streamed = mergeDeltas<OUT>(streamed, {
          ...delta,
          delta: { ...delta.delta },
        });
        yield delta;
      }
      // Store the finished result, as forward does.
      const finished = streamed[0];
      if (cachingFunction && cacheKey && finished) {
        try {
          await cachingFunction(cacheKey, finished.delta);
        } catch {}
      }
      return;
    }

    // For result picker, we need to buffer all results first
    const generator = this._forward1(ai, values, {
      ...options,
      stream: true,
    });

    let buffer: AxGenDeltaOut<OUT>[] = [];
    let currentVersion = 0;

    for await (const delta of generator) {
      if (delta.version !== currentVersion) {
        buffer = [];
      }
      currentVersion = delta.version;
      buffer = mergeDeltas<OUT>(buffer, delta);
    }

    // Use result picker to select from samples
    const selectedIndex = await selectFromSamples(
      buffer,
      {
        resultPicker: options?.resultPicker as
          | AxResultPickerFunction<OUT>
          | undefined,
      },
      // Pass memory to enable function result selection
      options?.mem,
      options?.sessionId
    );

    // Yield the selected result
    const selectedResult = buffer[selectedIndex];
    if (selectedResult) {
      const renderedDelta = await renderAudioOutputArtifacts(
        ai,
        this.signature,
        selectedResult.delta as unknown as AxGenOut,
        options
      );
      // Post-store cache
      if (cachingFunction && cacheKey) {
        try {
          await cachingFunction(cacheKey, renderedDelta);
        } catch {}
      }
      yield {
        version: currentVersion,
        index: selectedIndex,
        delta: renderedDelta as unknown as OUT,
      };
    }
  }

  public override setExamples(
    examples: Readonly<AxProgramExamples<IN, OUT>>,
    options?: Readonly<AxSetExamplesOptions>
  ) {
    super.setExamples(examples, options);
    // No need to update prompt template - all fields can be missing in examples
  }

  private isDebug(
    ai: Readonly<AxAIService>,
    options?: Readonly<AxProgramForwardOptions<any>>
  ) {
    return (
      options?.debug ??
      this.options?.debug ??
      ai.getOptions().debug ??
      axGlobals.debug ??
      false
    );
  }

  private getLogger(
    ai: Readonly<AxAIService>,
    options?: Readonly<AxProgramForwardOptions<any>>
  ) {
    return (
      options?.logger ??
      this.options?.logger ??
      ai.getOptions().logger ??
      axGlobals.logger ??
      ai.getLogger()
    );
  }

  private computeCacheKey(values: IN, inputNames: readonly string[]): string {
    const hasher = createHash('sha256');
    hasher.update(this.signature.hash() ?? '');

    const updateWithValue = (v: unknown): void => {
      const t = typeof v;
      hasher.update(`|${t}|`);
      if (v === null || v === undefined) {
        hasher.update('null');
        return;
      }
      if (t === 'string' || t === 'number' || t === 'boolean') {
        hasher.update(String(v));
        return;
      }
      if (Array.isArray(v)) {
        hasher.update('[');
        for (const item of v) updateWithValue(item);
        hasher.update(']');
        return;
      }
      if (
        typeof v === 'object' &&
        v !== null &&
        'mimeType' in (v as Record<string, unknown>) &&
        'data' in (v as Record<string, unknown>)
      ) {
        const mv = v as { mimeType?: string; data?: string };
        hasher.update(mv.mimeType ?? '');
        const dataDigest = createHash('sha256')
          .update(mv.data ?? '')
          .digest('hex');
        hasher.update(dataDigest);
        return;
      }
      if (typeof v === 'object') {
        const obj = v as Record<string, unknown>;
        const keys = Object.keys(obj).sort();
        for (const k of keys) {
          hasher.update(`{${k}}`);
          updateWithValue(obj[k]);
        }
        return;
      }
      hasher.update(String(v));
    };

    const row = inputNames.map((n) => (values as any)?.[n]);
    for (const val of row) updateWithValue(val);

    return hasher.digest('hex');
  }
}

export type AxGenerateErrorDetails = {
  model?: string;
  maxTokens?: number;
  streaming: boolean;
  signature: {
    input: Readonly<AxIField[]>;
    output: Readonly<AxIField[]>;
    description?: string;
  };
};

type ErrorOptions = { cause?: Error };

export class AxGenerateError extends Error {
  public readonly details: AxGenerateErrorDetails;

  constructor(
    message: string,
    details: Readonly<AxGenerateErrorDetails>,
    options?: ErrorOptions
  ) {
    super(message);
    this.name = 'AxGenerateError';
    this.details = details;
    // Set cause property dynamically to avoid TypeScript issues
    if (options?.cause) {
      (this as ErrorOptions).cause = options.cause;
    }
  }

  toJSON(): Record<string, unknown> {
    const cause = (this as ErrorOptions).cause;
    return {
      name: this.name,
      message: this.message,
      details: this.details,
      cause: cause
        ? {
            name: cause.name,
            message: cause.message,
            stack: cause.stack,
          }
        : undefined,
      stack: this.stack,
    };
  }
}

function enhanceError(
  e: unknown,
  ai: Readonly<AxAIService>,
  signature: Readonly<AxSignature>
): Error {
  const originalError = e instanceof Error ? e : new Error(String(e));

  // Never wrap abort errors — preserve the specific error type
  if (originalError instanceof AxAIServiceAbortedError) {
    return originalError;
  }

  if (originalError instanceof AxStreamingAssertionError) {
    return originalError;
  }

  // Validation and assertion errors propagate as they are. Every other failure
  // is wrapped, by its type, not by the words in its message: a processor
  // error that says "required" is still a generation failure.
  if (
    originalError.name === 'ValidationError' ||
    originalError.name === 'AxAssertionError'
  ) {
    return originalError;
  }

  const model = ai.getLastUsedChatModel() as string | undefined;
  const modelConfig = ai.getLastUsedModelConfig();

  const details = {
    model: model,
    maxTokens: modelConfig?.maxTokens,
    streaming: modelConfig?.stream ?? false,
    signature: {
      input: signature.getInputFields(),
      output: signature.getOutputFields(),
      description: signature.getDescription(),
    },
  };

  // Return custom error with short message and details as object property
  return new AxGenerateError(
    `Generate failed: ${originalError.message}`,
    details,
    {
      cause: originalError,
    }
  );
}
